
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import CopyButton from "./CopyChat";
import Chart from "../charts/Chart";
import axios from "axios";
import { useAgentConfig } from "../contexts/AgentConfigContext";
import { useDarkMode } from "../contexts/DarkModeContext";
import ChatMessageTabs from "./ChatMessageTabs";
import SourceCard from "./SourceCard";
import DocxPreviewModal from "./DocxPreviewModal";
import HighlightPreviewModal from "./HighlightPreviewModal";
import VideoPreviewModal from "./VideoPreviewModal";
import FilePreviewModal from "./FilePreviewModal";
import PdfPreviewModal from "./PdfPreviewModal";
import CodeBlock from "../common_jsx/CodeBlock";
import Tooltip from "../common_jsx/Tooltip";
import Options from "./Options";
import ClarificationOptions from "./ClarificationOptions";
import ReasoningSteps from "./ReasoningSteps";
import { parseJSONResponse, isJSONResponse } from "../utils/responseParser";
import { getTabConfig } from "../utils/tabConfig";
import { ThumbsUp, ThumbsDown, DollarSign, X, ChevronDown, ChevronUp, MessageSquare, Hash, HardDrive, Database, Globe, HelpCircle } from 'lucide-react';
import ServerSideDataTable from "./ServerSideDataTable";
import { parseMarkdownTable, parseHTMLTable } from "../utils/tableParser";
import { sanitizeHTML } from "../utils/sanitize";
import Spinner from "../utils/icons/Spinner";



/**
 * Helper function to parse message to array for Data tab display
 * Handles markdown tables, JSON strings, and arrays
 * @param {any} message - The message to parse
 * @param {number|null} limit - Max rows to return (null for all rows)
 */
const parseMessageToArray = (message, limit = null) => {
  let result = [];

  // If already an array, use it directly
  if (Array.isArray(message) && message.length > 0) {
    result = message;
  } else if (typeof message === 'string') {
    // HTML table (use includes so it works even when preceded by markdown headings)
    if (message.includes('<table')) {
      result = parseHTMLTable(message);
    }
    // Markdown table
    if (result.length === 0 && message.includes('|')) {
      const parsed = parseMarkdownTable(message);
      if (parsed.length > 0) result = parsed;
    }
    // JSON array
    if (result.length === 0) {
      try {
        const parsed = JSON.parse(message);
        if (Array.isArray(parsed)) result = parsed;
      } catch (e) {
        // Not valid JSON
      }
    }
    // **Key:** value markdown blocks — each `---` separated block becomes one row
    if (result.length === 0 && message.includes('**') && message.includes(':')) {
      const kvRe = /\*\*([^*]+?):\*\*\s*([^\n]*)/g;
      const blocks = message.split(/^\s*---\s*$/m);
      const rows = [];
      for (const block of blocks) {
        const row = {};
        let m;
        kvRe.lastIndex = 0;
        while ((m = kvRe.exec(block)) !== null) {
          const key = m[1].trim();
          const value = m[2].trim();
          if (key) row[key] = value;
        }
        if (Object.keys(row).length > 0) rows.push(row);
      }
      if (rows.length > 0) result = rows;
    }
  }

  // Apply limit if specified
  return limit !== null && result.length > limit ? result.slice(0, limit) : result;
};

// Google Chat-style smart timestamp formatting
const formatSmartTimestamp = (timestamp) => {
  if (!timestamp) return "";
  
  const ts = Number(timestamp);
  if (isNaN(ts)) return "";
  
  const messageDate = ts > 10000000000 ? new Date(ts) : new Date(ts * 1000);
  const now = new Date();
  
  // Helper to get start of day
  const getStartOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };
  
  const todayStart = getStartOfDay(now);
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const sevenDaysAgo = new Date(todayStart);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  const diffMs = now - messageDate;
  let diffMins = Math.floor(diffMs / 60000);
  // Clamp diffMins to a minimum of 0 to avoid negative values (future timestamps)
  if (diffMins < 0) diffMins = 0;
  
  
  // Today - within last 1 hour: relative time
  if (messageDate >= todayStart && diffMins < 60) {
    if (diffMins < 1) return "just now";
    return `${diffMins} min ago`;
  }
  
  // Today - older than 1 hour: time only
  if (messageDate >= todayStart) {
    return messageDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  }
  
  // Yesterday
  if (messageDate >= yesterdayStart && messageDate < todayStart) {
    const time = messageDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
    return `Yesterday, ${time}`;
  }
  
  // Within last 7 days: day name + time
  if (messageDate >= sevenDaysAgo) {
    const dayName = messageDate.toLocaleDateString('en-US', { weekday: 'short' });
    const time = messageDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
    return `${dayName}, ${time}`;
  }
  
  // Older than 7 days: date + time
  const date = messageDate.toLocaleDateString('en-US', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
  const time = messageDate.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit', 
    hour12: true 
  });
  return `${date}, ${time}`;
};

// Helper component for user avatar with error handling
const UserAvatar = ({ photoURL, userName, isDarkMode }) => {
  const [imageError, setImageError] = useState(false);
  
  // Get first letter of name for fallback
  const getInitial = (name) => {
    if (!name) return 'U';
    return name.charAt(0).toUpperCase();
  };

  // Reset error state when photoURL changes
  useEffect(() => {
    setImageError(false);
  }, [photoURL]);

  if (photoURL && !imageError) {
    return (
      <img 
        src={photoURL} 
        alt={userName || 'User'}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
        className={`w-9 h-9 rounded-full object-cover border-2 ${
          isDarkMode ? 'border-slate-700' : 'border-gray-200'
        }`}
        onError={() => {
          console.error('Failed to load avatar image:', photoURL);
          setImageError(true);
        }}
      />
    );
  }

  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
      isDarkMode 
        ? 'bg-gradient-to-br from-blue-500 to-purple-600 border-2 border-slate-700 shadow-lg' 
        : 'bg-gradient-to-br from-blue-400 to-purple-500 border-2 border-gray-200 shadow-md'
    }`}>
      {getInitial(userName)}
    </div>
  );
};

UserAvatar.propTypes = {
  photoURL: PropTypes.string,
  userName: PropTypes.string,
  isDarkMode: PropTypes.bool.isRequired,
};

// Dynamic timestamp component that updates automatically
const DynamicTimestamp = ({ timestamp, isDarkMode, isUserMessage }) => {
  const [formattedTime, setFormattedTime] = useState("");

  useEffect(() => {
    // If there is no timestamp, do not attempt to format or schedule updates
    if (!timestamp) {
      setFormattedTime("");
      return;
    }

    // Initial format
    setFormattedTime(formatSmartTimestamp(timestamp));

    // Only schedule updates if the timestamp is within the last hour (relative time)
    const now = new Date();
    const ts = Number(timestamp);
    const messageDate = ts > 10000000000 ? new Date(ts) : new Date(ts * 1000);
    const diffMins = Math.floor((now - messageDate) / 60000);

    let interval = null;
    if (diffMins < 60 && diffMins >= 0) {
      interval = setInterval(() => {
        setFormattedTime(formatSmartTimestamp(timestamp));
      }, 60000); // Update every 60 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timestamp]);

  if (!formattedTime) return null;

  return (
    <div className={`mt-1 text-xs ${isUserMessage ? 'text-right' : ''} ${
      isDarkMode ? 'text-slate-500' : 'text-gray-500'
    }`}>
      {formattedTime}
    </div>
  );
};

DynamicTimestamp.propTypes = {
  timestamp: PropTypes.number,
  isDarkMode: PropTypes.bool.isRequired,
  isUserMessage: PropTypes.bool,
};

// Code component for rendering code blocks
const CodeComponent = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || "");

  let childrenString = "";
  if (Array.isArray(children)) {
    childrenString = children.join("");
  } else if (children != null) {
    childrenString = String(children);
  }

  return !inline && match ? (
    <CodeBlock language={match[1]} code={childrenString.replace(/\n$/, "")} {...props} />
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

CodeComponent.propTypes = {
  node: PropTypes.object,
  inline: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]),
};

// Table component for rendering tables
const TableComponent = ({ node, className, children, ...props }) => {
  const { isDarkMode } = useDarkMode();
  
  return (
    <div className="overflow-x-auto table-scrollbar">
      <table 
        className={`min-w-full divide-y ${
          isDarkMode ? 'divide-slate-700' : 'divide-gray-200'
        }`}
        {...props}
      >
        {children}
      </table>
    </div>
  );
};

TableComponent.propTypes = {
  node: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
};

// Thead component for table headers
const TheadComponent = ({ children, ...props }) => {
  const { isDarkMode } = useDarkMode();
  
  return (
    <thead className={isDarkMode ? 'bg-slate-800' : 'bg-gray-50'} {...props}>
      {children}
    </thead>
  );
};

TheadComponent.propTypes = {
  children: PropTypes.node,
};

// Tbody component for table body
const TbodyComponent = ({ children, ...props }) => {
  const { isDarkMode } = useDarkMode();
  
  return (
    <tbody className={`divide-y ${
      isDarkMode ? 'divide-slate-700 bg-slate-900' : 'divide-gray-200 bg-white'
    }`} {...props}>
      {children}
    </tbody>
  );
};

TbodyComponent.propTypes = {
  children: PropTypes.node,
};

// Tr component for table rows
const TrComponent = ({ children, ...props }) => {
  const { isDarkMode } = useDarkMode();
  
  return (
    <tr className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'} {...props}>
      {children}
    </tr>
  );
};

TrComponent.propTypes = {
  children: PropTypes.node,
};

// Th component for table headers
const ThComponent = ({ children, ...props }) => {
  const { isDarkMode } = useDarkMode();
  
  return (
    <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
      isDarkMode ? 'text-white' : 'text-gray-700'
    }`} {...props}>
      {children}
    </th>
  );
};

ThComponent.propTypes = {
  children: PropTypes.node,
};

// Td component for table cells
const TdComponent = ({ children, ...props }) => {
  const { isDarkMode } = useDarkMode();
  
  return (
    <td className={`px-4 py-3 text-sm whitespace-nowrap ${
      isDarkMode ? 'text-slate-300' : 'text-gray-900'
    }`} {...props}>
      {children}
    </td>
  );
};

TdComponent.propTypes = {
  children: PropTypes.node,
};

// Helper component for rendering markdown with custom components
// Allow only safe URI schemes in markdown links to prevent javascript: XSS
const SAFE_HREF_RE = /^(https?:|mailto:|tel:|\/|#)/i;
const sanitizeHref = (href) => (href && SAFE_HREF_RE.test(href) ? href : '#');

// Logs a warning the first time a given key is seen, then stays silent —
// keeps the console readable when a render path keeps re-triggering.
const _warnOnceKeys = new Set();
const logging_warn_once = (key, ...args) => {
  if (_warnOnceKeys.has(key)) return;
  _warnOnceKeys.add(key);
  // eslint-disable-next-line no-console
  console.warn(key, ...args);
};

// Tracks which citation chunk is currently being opened so its pill can show a
// spinner and ignore further clicks until the preview resolves. Provided by the
// message component and consumed by the citation pills in MarkdownRenderer.
const CitationLoadingContext = React.createContext(null);

// Provides web_sources (Gemini google_search grounding) to the markdown
// renderer so inline `#gs-1,2,3` markers can be expanded into rich source
// pills (favicon + site + overflow).
const WebSourcesContext = React.createContext(null);

// Friendly site label for common hosts; falls back to the bare hostname.
const SITE_LABELS = {
  'reddit.com': 'Reddit',
  'youtube.com': 'YouTube',
  'youtu.be': 'YouTube',
  'twitter.com': 'X',
  'x.com': 'X',
  'github.com': 'GitHub',
  'stackoverflow.com': 'Stack Overflow',
  'wikipedia.org': 'Wikipedia',
  'medium.com': 'Medium',
  'linkedin.com': 'LinkedIn',
};

const getHost = (uri) => {
  try { return new URL(uri).hostname.replace(/^www\./, ''); } catch { return ''; }
};

// Gemini grounding URIs are vertexaisearch.cloud.google.com redirect links —
// useless for display. We hide that host and rely on the title/domain instead.
const VERTEX_REDIRECT_HOST = 'vertexaisearch.cloud.google.com';

// Best display host for a source: prefer the explicit `domain` Google sometimes
// returns; otherwise try to recover a domain from the trailing "- site.com" in
// the title; otherwise parse the URI but discard the Vertex redirect host.
// Returns '' when no real host is recoverable.
const sourceHost = (source) => {
  if (source.domain) return source.domain.replace(/^www\./, '');
  const titleTail = source.title?.match(/[-–—]\s*([a-z0-9.-]+\.[a-z]{2,})\s*$/i);
  if (titleTail) return titleTail[1].toLowerCase();
  const host = getHost(source.uri);
  if (host && host !== VERTEX_REDIRECT_HOST) return host;
  return '';
};

// Host used to fetch the favicon — falls back to the URI's host (typically
// Google's, since the redirect goes through vertexaisearch.cloud.google.com),
// so the pill is never iconless.
const faviconHost = (source) => sourceHost(source) || getHost(source.uri);

const siteLabel = (source) => {
  const title = source.title?.trim();
  if (title) return title;
  const host = sourceHost(source);
  if (host) {
    const parts = host.split('.');
    const baseTwo = parts.slice(-2).join('.');
    return SITE_LABELS[host] || SITE_LABELS[baseTwo] || host;
  }
  return `Citation ${source.index}`;
};

// Hover popover that lists every grounding source for a pill, each individually
// clickable. Receives the parent's enter/leave handlers so moving the cursor
// from the pill onto the popover doesn't dismiss it.
const GroundingPopover = ({ sources, isDarkMode, onMouseEnter, onMouseLeave }) => {
  const panelBg = isDarkMode
    ? 'bg-slate-900 border-slate-700 text-slate-200'
    : 'bg-white border-gray-200 text-gray-800';
  const rowHover = isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50';
  const subText = isDarkMode ? 'text-slate-400' : 'text-gray-500';
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`absolute z-50 left-0 top-full mt-1 w-72 rounded-lg border shadow-lg p-1.5 ${panelBg}`}
    >
      <ul className="space-y-0.5 max-h-72 overflow-y-auto">
        {sources.map((s) => {
          const host = sourceHost(s);
          const iconHost = faviconHost(s);
          const favicon = iconHost
            ? `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(iconHost)}`
            : null;
          const title = s.title?.trim() || siteLabel(s);
          return (
            <li key={`${s.index}-${s.uri}`}>
              <a
                href={s.uri}
                target="_blank"
                rel="noopener noreferrer"
                className={`no-underline flex items-start gap-2 px-2 py-1.5 rounded-md ${rowHover}`}
              >
                {favicon && (
                  <img
                    src={favicon}
                    alt=""
                    width={14}
                    height={14}
                    className="rounded-sm mt-0.5 flex-none"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium leading-snug line-clamp-2">
                    {title}
                  </div>
                  {host && (
                    <div className={`text-[10px] truncate mt-0.5 ${subText}`}>
                      {host}
                    </div>
                  )}
                </div>
                <span className={`text-[10px] font-semibold flex-none mt-0.5 ${subText}`}>
                  {s.index}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

GroundingPopover.propTypes = {
  sources: PropTypes.array.isRequired,
  isDarkMode: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

// Inline grounding-source pill (Perplexity-style):
//   [favicon] <source title> +N
const GroundingPill = ({ sources, isDarkMode }) => {
  const [open, setOpen] = React.useState(false);
  const closeTimer = React.useRef(null);
  React.useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  if (!sources || sources.length === 0) return null;
  const primary = sources[0];
  const iconHost = faviconHost(primary);
  const favicon = iconHost
    ? `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(iconHost)}`
    : null;
  const label = siteLabel(primary);
  const overflow = sources.length - 1;
  const pillBg = isDarkMode
    ? 'bg-slate-800 border-slate-700 hover:bg-slate-700'
    : 'bg-gray-100 border-gray-200 hover:bg-gray-200';
  const textColor = isDarkMode ? 'text-slate-200' : 'text-gray-700';
  const overflowColor = isDarkMode ? 'text-slate-400' : 'text-gray-500';

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <span
      className="relative inline-block align-baseline mx-0.5"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <a
        href={primary.uri}
        target="_blank"
        rel="noopener noreferrer"
        className={`no-underline inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[11px] leading-none transition-colors ${pillBg} ${textColor}`}
      >
        {favicon && (
          <img
            src={favicon}
            alt=""
            width={12}
            height={12}
            className="rounded-sm flex-none"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <span className="font-medium truncate max-w-[160px]">{label}</span>
        {overflow > 0 && (
          <span className={`font-semibold ${overflowColor}`}>+{overflow}</span>
        )}
      </a>
      {open && (
        <GroundingPopover
          sources={sources}
          isDarkMode={isDarkMode}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        />
      )}
    </span>
  );
};

GroundingPill.propTypes = {
  sources: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.number,
    uri: PropTypes.string,
    title: PropTypes.string,
    domain: PropTypes.string,
  })),
  isDarkMode: PropTypes.bool,
};

// Reads the `#gs-1,2,3` marker emitted by the Gemini adapter and resolves the
// indices against the web_sources list in WebSourcesContext.
const GroundingPillFromMarker = ({ href, isDarkMode }) => {
  const webSources = React.useContext(WebSourcesContext);
  if (!webSources || webSources.length === 0) return null;
  const indices = href
    .slice(4)
    .split(',')
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isFinite(n));
  const byIndex = new Map(webSources.map((s) => [s.index, s]));
  const resolved = indices.map((i) => byIndex.get(i)).filter(Boolean);
  if (resolved.length === 0) return null;
  return <GroundingPill sources={resolved} isDarkMode={isDarkMode} />;
};

GroundingPillFromMarker.propTypes = {
  href: PropTypes.string.isRequired,
  isDarkMode: PropTypes.bool,
};

// Citation pill: while any citation is opening, the one being loaded shows a
// spinner and the rest are disabled, so a user can't fire off duplicate
// open-requests by clicking repeatedly before the preview modal appears.
const CitationPill = ({ chunkIdx, onChunkClick, pillClass, children }) => {
  const loadingChunkIdx = React.useContext(CitationLoadingContext);
  const isLoading = loadingChunkIdx === chunkIdx;
  const isBusy = loadingChunkIdx != null;
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); if (!isBusy) onChunkClick(chunkIdx); }}
      disabled={isBusy}
      title="View source"
      className={`${pillClass} transition-opacity ${isBusy ? 'cursor-wait opacity-60' : 'cursor-pointer hover:opacity-80'}`}
    >
      {isLoading ? <span className="inline-block w-2.5 h-2.5 border-2 border-current border-t-transparent rounded-full animate-spin" /> : children}
    </button>
  );
};

CitationPill.propTypes = {
  chunkIdx: PropTypes.number.isRequired,
  onChunkClick: PropTypes.func.isRequired,
  pillClass: PropTypes.string,
  children: PropTypes.node,
};

// Renders Google Search grounding output:
//   - A "Web sources" list keyed by the same [N] indices the Gemini adapter
//     inlined into the answer text as [N](uri) markdown links.
//   - The `search_entry_point.rendered_content` HTML chip, which Google's ToS
//     requires us to display verbatim when google_search grounding is used.
//     See https://ai.google.dev/gemini-api/docs/google-search
// The HTML chip ships its own <style> rules, so we inject it raw rather than
// running it through DOMPurify (which would strip the inline <style> block).
const WebGroundingPanel = ({ searchEntryPointHtml, isDarkMode }) => {
  const hasChip = typeof searchEntryPointHtml === 'string' && searchEntryPointHtml.length > 0;
  if (!hasChip) return null;
  const borderColor = isDarkMode ? 'border-slate-700' : 'border-gray-200';
  return (
    <div className={`mt-3 pt-3 border-t ${borderColor}`}>
      <div
        // ToS-mandated Google Search Suggestions chip — render verbatim.
        dangerouslySetInnerHTML={{ __html: searchEntryPointHtml }}
      />
    </div>
  );
};

WebGroundingPanel.propTypes = {
  searchEntryPointHtml: PropTypes.string,
  isDarkMode: PropTypes.bool,
};

const MarkdownRenderer = ({ content, onChunkClick }) => {
  const { isDarkMode } = useDarkMode();

  if (typeof content !== "string") {
    if (content == null || content === false) return null;
    if (React.isValidElement(content)) return content;
    // Plain JS object/array slipped through (e.g. an unparsed insights payload
    // like `{insight, data_source, source, suggested_questions, pagination}`).
    // Returning it directly throws "Objects are not valid as a React child"
    // and crashes the message tree — stringify it so the page survives.
    if (typeof content === "object") {
      logging_warn_once(
        "MarkdownRenderer received a non-element object as content; stringifying",
        content,
      );
      try {
        return (
          <pre className="whitespace-pre-wrap text-xs">
            {JSON.stringify(content, null, 2)}
          </pre>
        );
      } catch {
        return null;
      }
    }
    return String(content);
  }

  return (
    <div className={isDarkMode ? 'text-slate-200' : 'text-slate-800'}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code: ({ node, inline, className, children, ...props }) => {
            if (inline) {
              // Normalize children for inline code to avoid rendering artifacts:
              // - If children is an array, join its elements as strings (ignoring null/undefined).
              // - If children is null or undefined, treat as an empty string.
              // This matches the logic in CodeComponent and prevents commas or other artifacts.
              let childrenString = '';
              if (Array.isArray(children)) {
                childrenString = children.filter(c => c != null).join('');
              } else if (children != null) {
                childrenString = String(children);
              }
              childrenString = childrenString.replace(/\n$/, '');
              return (
                <code 
                  className={`px-2 py-1 rounded-md font-mono text-xs font-medium ${
                    isDarkMode 
                      ? 'bg-slate-700/80 text-rose-300 border border-slate-600/50' 
                      : 'bg-slate-100 text-rose-600 border border-slate-200'
                  }`}
                  {...props}
                >
                  {childrenString}
                </code>
              );
            }
            return <CodeComponent node={node} inline={inline} className={className} {...props}>{children}</CodeComponent>;
          },
          table: TableComponent,
          thead: TheadComponent,
          tbody: TbodyComponent,
          tr: TrComponent,
          th: ThComponent,
          td: TdComponent,
          strong: ({ children }) => (
            <strong className={`font-semibold ${isDarkMode ? 'text-slate-50' : 'text-slate-900'}`}>
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>
              {children}
            </em>
          ),
          h1: ({ children }) => (
            <h1 className={`text-3xl font-bold mt-4 mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className={`text-2xl font-bold mt-4 mb-2.5 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className={`text-lg font-bold mt-3 first:mt-0 mb-3 tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className={`text-base font-semibold mt-3 mb-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
              {children}
            </h4>
          ),
          a: ({ children, href }) => {
            const pillClass = `inline-flex items-center justify-center align-super text-[10px] font-semibold leading-none rounded-full w-4 h-4 mx-0.5 ${
              isDarkMode ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-700'
            }`;
            if (href && href.startsWith('#cite-')) {
              const chunkIdx = parseInt(href.slice(6), 10);
              if (onChunkClick) {
                return <CitationPill chunkIdx={chunkIdx} onChunkClick={onChunkClick} pillClass={pillClass}>{children}</CitationPill>;
              }
              return <span className={pillClass}>{children}</span>;
            }
            // Gemini google_search grounding marker: `[source](#gs-1,2,3)` where
            // the indices are 1-based into reference.web_sources. Resolve them
            // and render a Perplexity-style pill (favicon + site + overflow).
            if (href && href.startsWith('#gs-')) {
              return <GroundingPillFromMarker href={href} isDarkMode={isDarkMode} />;
            }
            // Legacy fallback: older persisted messages were generated before the
            // backend switched to `#gs-` markers and contain `[N](https://...)`
            // where N is the citation number. Render those as a single-source
            // pill so old conversations don't regress to plain blue numbers.
            const childText = Array.isArray(children)
              ? children.filter((c) => typeof c === 'string').join('')
              : (typeof children === 'string' ? children : '');
            const numericText = childText.trim();
            if (href && /^https?:\/\//i.test(href) && /^\d+$/.test(numericText)) {
              return (
                <GroundingPill
                  sources={[{ index: parseInt(numericText, 10), uri: href, title: '' }]}
                  isDarkMode={isDarkMode}
                />
              );
            }
            return (
              <a
                href={sanitizeHref(href)}
                className={`font-medium hover:underline ${isDarkMode ? 'text-sky-400 hover:text-sky-300' : 'text-sky-600 hover:text-sky-700'}`}
                rel="noopener noreferrer"
                {...(href && href.startsWith('http') ? { target: '_blank' } : {})}
              >
                {children}
              </a>
            );
          },
          li: ({ children }) => (
            <li className={`mb-2.5 leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
              {children}
            </li>
          ),
          p: ({ children }) => (
            <p className={`mb-4 leading-relaxed ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className={`list-disc pl-7 mb-5 mt-2 space-y-1 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className={`list-decimal pl-7 mb-5 mt-2 space-y-1 ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
              {children}
            </ol>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

MarkdownRenderer.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onChunkClick: PropTypes.func,
};

// Helper component for rendering table data
const TableRenderer = ({ message, id, setPaginationState }) => {
  const { isDarkMode } = useDarkMode();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10); // Removed unused setRowsPerPage
  const [tableData, setTableData] = useState({ headers: [], dataRows: [], totalRows: 0 });
  const [error, setError] = useState(false);

  // Parse table data
  useEffect(() => {
    try {
      const rows = message.trim().split("\n");
      const headers = rows[0]
        .split("|")
        .filter((cell) => cell.trim())
        .map((cell) => cell.trim());

      const dataRows = rows.slice(2).map((row) => {
        return row
          .split("|")
          .filter((cell) => cell.trim())
          .map((cell) => cell.trim());
      });

      setTableData({ headers, dataRows, totalRows: dataRows.length });
      setError(false);
    } catch (e) {
      console.error("Error parsing table:", e);
      setError(true);
    }
  }, [message]);

  const totalPages = Math.ceil(tableData.totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, tableData.totalRows);
  const currentRows = tableData.dataRows.slice(startIndex, endIndex);
  const showPagination = tableData.totalRows > 10;

  // Update pagination state for toolbar
  useEffect(() => {
    if (setPaginationState && showPagination) {
      setPaginationState({
        isLoading: false,
        pageNum: currentPage,
        totalPages,
        startRecord: startIndex + 1,
        endRecord: endIndex,
        totalRows: tableData.totalRows,
        handlePageChange: (newPage) => {
          if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
          }
        },
        showPagination: true,
      });
    } else if (setPaginationState) {
      setPaginationState({ showPagination: false });
    }
  }, [currentPage, totalPages, startIndex, endIndex, tableData.totalRows, showPagination, setPaginationState]);

  if (error) {
    return <MarkdownRenderer content={message} />;
  }

  return (
    <div className="w-full">
      {/* Table */}
      <div className="overflow-x-auto table-scrollbar">
        <table className={`min-w-full divide-y ${
          isDarkMode ? 'divide-slate-700' : 'divide-gray-200'
        }`}>
          <thead className={isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}>
            <tr>
              {tableData.headers.map((header, headerIndex) => (
                <th key={`header-${header}-${headerIndex}`} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-white' : 'text-gray-700'
                }`}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody className={`divide-y ${
            isDarkMode ? 'divide-slate-700 bg-slate-900' : 'divide-gray-200 bg-white'
          }`}>
            {currentRows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}-${row[0] || rowIndex}`} className={isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'}>
                {row.map((cell, cellIndex) => (
                  <td key={`cell-${rowIndex}-${cellIndex}-${cell}`} className={`px-4 py-3 text-sm whitespace-nowrap ${
                    isDarkMode ? 'text-slate-300' : 'text-gray-900'
                  }`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

TableRenderer.propTypes = {
  message: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setPaginationState: PropTypes.func,
};

// Helper component for rendering table with Tailwind pagination
// Helper component for rendering SQL pagination content
// Always uses ServerSideDataTable for consistent pagination UI (rows-per-page, page numbers, go-to).
// For small datasets (<=30 rows), all data is already in initialData so no API calls are made.
const SQLPaginationContent = ({
  displayData,
  totalRows,
  id,
  setPaginationState,
  sessionId,
}) => {
  return (
    <ServerSideDataTable
      initialData={displayData || []}
      totalCount={totalRows || 0}
      messageId={id}
      sessionId={sessionId}
      onPaginationStateChange={setPaginationState}
    />
  );
};

SQLPaginationContent.propTypes = {
  displayData: PropTypes.array,
  totalRows: PropTypes.number,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setPaginationState: PropTypes.func,
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// Helper component for rendering multiple data source tables as sub-tabs
const MultiSourceTabs = ({ perSourceData, effectiveTotalRows, id, sessionId, setPaginationState, onActiveSourceChange }) => {
  const { isDarkMode } = useDarkMode();
  const sourceKeys = Object.keys(perSourceData);
  const [activeSource, setActiveSource] = useState(sourceKeys[0] || '');

  // Intentionally mount-only: notify parent of the initial active source once.
  // Adding onActiveSourceChange would re-fire if the parent re-renders without memoizing the
  // callback; adding sourceKeys[0] would re-notify on every data update — both undesired.
  useEffect(() => {
    if (onActiveSourceChange && sourceKeys[0]) {
      onActiveSourceChange(sourceKeys[0]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSourceChange = (key) => {
    setActiveSource(key);
    if (onActiveSourceChange) onActiveSourceChange(key);
  };

  // Single source: render directly without header (no step name shown)
  if (sourceKeys.length === 1) {
    const key = sourceKeys[0];
    const data = perSourceData[key];
    return (
      <ServerSideDataTable
        initialData={data}
        totalCount={
          typeof effectiveTotalRows === 'object' && effectiveTotalRows !== null
            ? (effectiveTotalRows[key] || data.length)
            : (effectiveTotalRows || data.length)
        }
        messageId={id}
        sessionId={sessionId}
        sourceKey={key}
        onPaginationStateChange={setPaginationState}
      />
    );
  }

  const activeData = perSourceData[activeSource] || [];

  return (
    <div>
      <div className={`flex items-center gap-1 mb-3 pb-2 border-b ${
        isDarkMode ? 'border-slate-700' : 'border-gray-200'
      }`}>
        {sourceKeys.map((key, index) => (
          <button
            key={key}
            type="button"
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
              activeSource === key
                ? (isDarkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-900')
                : (isDarkMode ? 'text-slate-400 hover:text-white hover:bg-slate-700/50' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50')
            }`}
            onClick={() => handleSourceChange(key)}
          >
            Output {index + 1}
          </button>
        ))}
      </div>
      <ServerSideDataTable
        key={activeSource}
        initialData={activeData}
        totalCount={
          typeof effectiveTotalRows === 'object' && effectiveTotalRows !== null
            ? (effectiveTotalRows[activeSource] || activeData.length)
            : (effectiveTotalRows || activeData.length)
        }
        messageId={id}
        sessionId={sessionId}
        sourceKey={activeSource}
        onPaginationStateChange={null}
      />
    </div>
  );
};

MultiSourceTabs.propTypes = {
  perSourceData: PropTypes.object.isRequired,
  effectiveTotalRows: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setPaginationState: PropTypes.func,
  onActiveSourceChange: PropTypes.func,
};

// Helper component for rendering message content
const MessageContent = ({
  message,
  isTable,
  containsMarkdown,
  id,
  handleSend,
  isAuthorAdmin,
  clarificationOptions,
  handleClarificationOptionClick,
  clarificationOptionsDisabled,
  selectedClarificationOption,
  setPaginationState,
  onChunkClick,
}) => {
  const { isDarkMode } = useDarkMode();

  if (message === false) {
    return <span className={isDarkMode ? 'text-slate-200' : 'text-gray-900'}>Apologies</span>;
  }

  const renderMessageContent = () => {
    // HTML string: render as sanitized HTML (e.g. key_insights pre-formatted as HTML)
    if (typeof message === 'string' && message.trimStart().startsWith('<')) {
      return (
        <>
          <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(message) }} />
          {isAuthorAdmin && clarificationOptions && clarificationOptions.length > 0 && (
            <div className="mt-3">
              <ClarificationOptions
                options={clarificationOptions}
                onOptionClick={handleClarificationOptionClick}
                disabled={clarificationOptionsDisabled}
                selectedOption={selectedClarificationOption}
              />
            </div>
          )}
        </>
      );
    }

    // Check if message is a JSON response that needs parsing
    if (isJSONResponse(message)) {
      const parsedContent = parseJSONResponse(message);
      return (
        <>
          <MarkdownRenderer content={parsedContent} onChunkClick={onChunkClick} />
          {isAuthorAdmin && clarificationOptions && clarificationOptions.length > 0 && (
            <ClarificationOptions
              options={clarificationOptions}
              onOptionClick={handleClarificationOptionClick}
              disabled={clarificationOptionsDisabled}
              selectedOption={selectedClarificationOption}
            />
          )}
        </>
      );
    }

    if (isTable()) {
      return <TableRenderer message={message} id={id} setPaginationState={setPaginationState} />;
    }

    if (containsMarkdown()) {
      return (
        <>
          <MarkdownRenderer content={message} onChunkClick={onChunkClick} />
          {isAuthorAdmin && clarificationOptions && clarificationOptions.length > 0 && (
            <ClarificationOptions
              options={clarificationOptions}
              onOptionClick={handleClarificationOptionClick}
              disabled={clarificationOptionsDisabled}
              selectedOption={selectedClarificationOption}
            />
          )}
        </>
      );
    }

    return (
      <>
        <div
          className={`whitespace-pre-wrap break-words ${
            isDarkMode ? 'text-slate-200' : 'text-gray-900'
          }`}
        >
          {Array.isArray(message)
            ? message.map((key, index) => (
                <div key={`msg-item-${key}-${index}`}>
                  <button
                    type="button"
                    className={`p-0 text-left underline cursor-pointer ${
                      isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                    }`}
                    onClick={() => handleSend(key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSend(key);
                      }
                    }}
                  >
                    {index + 1}. {key}
                  </button>
                </div>
              ))
            : message}
        </div>
        {isAuthorAdmin && clarificationOptions && clarificationOptions.length > 0 && (
          <div className="mt-3">
            <ClarificationOptions
              options={clarificationOptions}
              onOptionClick={handleClarificationOptionClick}
              disabled={clarificationOptionsDisabled}
              selectedOption={selectedClarificationOption}
            />
          </div>
        )}
      </>
    );
  };

  return renderMessageContent();
};

MessageContent.propTypes = {
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object, PropTypes.bool]).isRequired,
  isTable: PropTypes.func.isRequired,
  containsMarkdown: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleSend: PropTypes.func.isRequired,
  isAuthorAdmin: PropTypes.bool.isRequired,
  clarificationOptions: PropTypes.array,
  handleClarificationOptionClick: PropTypes.func,
  clarificationOptionsDisabled: PropTypes.bool,
  selectedClarificationOption: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  setPaginationState: PropTypes.func,
  onChunkClick: PropTypes.func,
};

// Helper component for rendering tab content
const TabContent = ({
  selectedTab,
  id,
  isSQLDatatype,
  isInsightsDatatype,
  totalRows,
  displayData,
  message,
  isTable,
  containsMarkdown,
  handleSend,
  isAuthorAdmin,
  clarificationOptions,
  handleClarificationOptionClick,
  clarificationOptionsDisabled,
  selectedClarificationOption,
  chartData,
  setChartData,
  source,
  datatype,
  description,
  curl_commands,
  setPaginationState,
  sessionId,
  isStreaming,
  perSourceData,
  effectiveTotalRows,
  onChunkClick,
  onActiveSourceChange,
}) => {
  const { isDarkMode } = useDarkMode();

  // Centralized processing state detection
  // Check if message or description contains processing/loading indicators
  const isProcessing = 
    (typeof message === 'string' && 
     (message.includes('Processing your request') || 
      message.includes('is running') ||
      message.includes('Analyzing') ||
      message.includes('Sub-agent') ||
      (message.includes('...') && message.length < 100))) ||
    (typeof description === 'string' && 
     (description.includes('is running') || 
      description.includes('Processing') ||
      description.includes('Analyzing') ||
      description.includes('Sub-agent') ||
      description.includes('task complexity')));
  
  // Determine which content to show during processing (prefer description if it has processing info)
  const processingContent = 
    (typeof description === 'string' && 
     (description.includes('Analyzing') || description.includes('Sub-agent') || description.includes('Processing') || description.includes('is running'))) 
      ? description 
      : (message || description || 'Processing...');
  
  // Use pagination for SQL/Insights when totalRows is known.
  // SQLPaginationContent always uses ServerSideDataTable, which serves small datasets
  // entirely from its initialData cache and calls the pagination API only for larger ones.
  const shouldUsePagination = (isSQLDatatype || isInsightsDatatype) && totalRows != null;

  switch (selectedTab) {
    case "data":
      return (
        <div id={id}>
          {isProcessing ? (
            // Show centralized processing message in Data tab
            <div className={`whitespace-pre-wrap break-words ${
              isDarkMode ? 'text-slate-200' : 'text-gray-900'
            }`}>
              <MarkdownRenderer content={processingContent} />
            </div>
          ) : shouldUsePagination && perSourceData ? (
            // Multi-table pagination: tabbed interface per data source
            <MultiSourceTabs
              perSourceData={perSourceData}
              effectiveTotalRows={effectiveTotalRows}
              id={id}
              sessionId={sessionId}
              setPaginationState={setPaginationState}
              onActiveSourceChange={onActiveSourceChange}
            />
          ) : shouldUsePagination ? (
            // Single-table pagination (legacy / fallback)
            <SQLPaginationContent
              displayData={displayData}
              totalRows={typeof effectiveTotalRows === 'number' ? effectiveTotalRows : (displayData?.length || 0)}
              id={id}
              setPaginationState={setPaginationState}
              sessionId={sessionId}
            />
          ) : (isSQLDatatype || isInsightsDatatype) && (!displayData || displayData.length === 0) ? (
            // No data available to display
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              No records to display
            </p>
          ) : (
            <MessageContent
              message={message}
              isTable={isTable}
              containsMarkdown={containsMarkdown}
              id={id}
              handleSend={handleSend}
              isAuthorAdmin={isAuthorAdmin}
              clarificationOptions={clarificationOptions}
              handleClarificationOptionClick={handleClarificationOptionClick}
              clarificationOptionsDisabled={clarificationOptionsDisabled}
              selectedClarificationOption={selectedClarificationOption}
              setPaginationState={setPaginationState}
              onChunkClick={onChunkClick}
            />
          )}
        </div>
      );

    case "chart":
      return message && message.length > 0 ? (
        <Chart source={id} data={message} chartData={chartData} setChartData={setChartData} />
      ) : (
        <p>Data required for Chart</p>
      );

    case "source":
      return <SourceTabContent source={source} isSQLDatatype={isSQLDatatype} datatype={datatype} id={id} curl_commands={curl_commands} isStreaming={isStreaming} />;

    case "description":
      return (
        <DescriptionTabContent
          description={description}
          message={message}
          isSQLDatatype={isSQLDatatype}
          datatype={datatype}
          source={source}
          id={id}
          isTable={isTable}
          containsMarkdown={containsMarkdown}
          handleSend={handleSend}
          isAuthorAdmin={isAuthorAdmin}
          clarificationOptions={clarificationOptions}
          handleClarificationOptionClick={handleClarificationOptionClick}
          clarificationOptionsDisabled={clarificationOptionsDisabled}
          selectedClarificationOption={selectedClarificationOption}
          setPaginationState={setPaginationState}
          isProcessing={isProcessing}
          processingContent={processingContent}
          isStreaming={isStreaming}
          onChunkClick={onChunkClick}
        />
      );

    case "select":
      return datatype === "error" ? <>No Source to display.</> : null;

    default:
      return null;
  }
};

TabContent.propTypes = {
  selectedTab: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isInsightsDatatype: PropTypes.bool,
  isSQLDatatype: PropTypes.bool.isRequired,
  totalRows: PropTypes.number,
  displayData: PropTypes.array,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
  isTable: PropTypes.func.isRequired,
  containsMarkdown: PropTypes.func.isRequired,
  handleSend: PropTypes.func.isRequired,
  isAuthorAdmin: PropTypes.bool.isRequired,
  clarificationOptions: PropTypes.array,
  handleClarificationOptionClick: PropTypes.func,
  clarificationOptionsDisabled: PropTypes.bool,
  selectedClarificationOption: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  chartData: PropTypes.array.isRequired,
  setChartData: PropTypes.func.isRequired,
  source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  datatype: PropTypes.string,
  description: PropTypes.string,
  curl_commands: PropTypes.array,
  setPaginationState: PropTypes.func,
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isStreaming: PropTypes.bool,
  onChunkClick: PropTypes.func,
  onActiveSourceChange: PropTypes.func,
};

// Helper function to highlight bash/curl syntax
const highlightBash = (line, isDarkMode) => {
  if (!line || line.trim() === '') return line || '\u00A0';
  
  // Bash/curl keywords and options
  const keywords = /\b(curl|grep|awk|sed|cat|echo|export|if|then|else|fi|for|while|do|done|function)\b/g;
  const flags = /(-[a-zA-Z]|--[a-zA-Z-]+)/g;
  
  // String literals (single and double quotes)
  const stringRegex = /(["'][^"']*["'])/g;
  const strings = [];
  let match;
  
  while ((match = stringRegex.exec(line)) !== null) {
    strings.push({ start: match.index, end: match.index + match[0].length, text: match[0] });
  }
  
  // Process line
  let currentIndex = 0;
  const segments = [];
  
  strings.forEach((str) => {
    if (str.start > currentIndex) {
      segments.push({ text: line.substring(currentIndex, str.start), type: 'code' });
    }
    segments.push({ text: str.text, type: 'string' });
    currentIndex = str.end;
  });
  
  if (currentIndex < line.length) {
    segments.push({ text: line.substring(currentIndex), type: 'code' });
  }
  
  // Render segments
  return (
    <>
      {segments.map((segment, idx) => {
        if (segment.type === 'string') {
          return <span key={idx} style={{ color: isDarkMode ? '#10b981' : '#198754' }}>{segment.text}</span>;
        } else {
          // Highlight keywords and flags in code segments
          let processedText = segment.text;
          const elements = [];
          let lastIdx = 0;
          
          // Find all keywords and flags
          const allMatches = [];
          let keywordMatch;
          const keywordRegex = new RegExp(keywords.source, 'g');
          while ((keywordMatch = keywordRegex.exec(processedText)) !== null) {
            allMatches.push({ start: keywordMatch.index, end: keywordMatch.index + keywordMatch[0].length, text: keywordMatch[0], type: 'keyword' });
          }
          
          let flagMatch;
          const flagRegex = new RegExp(flags.source, 'g');
          while ((flagMatch = flagRegex.exec(processedText)) !== null) {
            allMatches.push({ start: flagMatch.index, end: flagMatch.index + flagMatch[0].length, text: flagMatch[0], type: 'flag' });
          }
          
          // Sort by position
          allMatches.sort((a, b) => a.start - b.start);
          
          allMatches.forEach((m, mIdx) => {
            if (m.start > lastIdx) {
              elements.push(<span key={`${idx}-text-${mIdx}`}>{processedText.substring(lastIdx, m.start)}</span>);
            }
            if (m.type === 'keyword') {
              elements.push(<span key={`${idx}-kw-${mIdx}`} style={{ color: isDarkMode ? '#8b5cf6' : '#6f42c1', fontWeight: '600' }}>{m.text}</span>);
            } else {
              elements.push(<span key={`${idx}-flag-${mIdx}`} style={{ color: isDarkMode ? '#3b82f6' : '#0d6efd', fontWeight: '500' }}>{m.text}</span>);
            }
            lastIdx = m.end;
          });
          
          if (lastIdx < processedText.length) {
            elements.push(<span key={`${idx}-end`}>{processedText.substring(lastIdx)}</span>);
          }
          
          return elements;
        }
      })}
    </>
  );
};

// Helper function to highlight SQL syntax
const highlightSQL = (line, isDarkMode) => {
  if (!line || line.trim() === '') return line || '\u00A0';
  
  // SQL keywords
  const keywords = /\b(SELECT|FROM|WHERE|JOIN|INNER|LEFT|RIGHT|OUTER|ON|AND|OR|IN|AS|BETWEEN|GROUP BY|ORDER BY|HAVING|LIMIT|OFFSET|INSERT|UPDATE|DELETE|CREATE|DROP|ALTER|TABLE|INDEX|VIEW|DATABASE|UNION|ALL|DISTINCT|COUNT|SUM|AVG|MIN|MAX|COALESCE|CASE|WHEN|THEN|ELSE|END|CAST|DATE_TRUNC|INTERVAL|DATE|NULL|IS|NOT|LIKE|EXISTS|WITH)\b/gi;
  
  // Comments (-- style)
  const commentMatch = line.match(/^(\s*)(--.*)/);
  if (commentMatch) {
    return (
      <>
        {commentMatch[1]}
        <span style={{ color: isDarkMode ? '#6b7280' : '#6c757d', fontStyle: 'italic' }}>
          {commentMatch[2]}
        </span>
      </>
    );
  }
  
  // Chain of Thought line
  if (line.includes('--- Chain of Thought')) {
    return <span style={{ color: isDarkMode ? '#6b7280' : '#6c757d', fontStyle: 'italic' }}>{line}</span>;
  }
  
  // String literals
  const stringRegex = /'[^']*'/g;
  const strings = [];
  let match;
  
  while ((match = stringRegex.exec(line)) !== null) {
    strings.push({ start: match.index, end: match.index + match[0].length, text: match[0] });
  }
  
  // Process line with keyword and string highlighting
  let processedLine = line;
  const segments = [];
  let currentIndex = 0;
  
  // Split by strings first
  strings.forEach((str, idx) => {
    if (str.start > currentIndex) {
      // Process non-string part for keywords
      const beforeString = processedLine.substring(currentIndex, str.start);
      segments.push({ text: beforeString, type: 'code' });
    }
    segments.push({ text: str.text, type: 'string' });
    currentIndex = str.end;
  });
  
  if (currentIndex < processedLine.length) {
    segments.push({ text: processedLine.substring(currentIndex), type: 'code' });
  }
  
  // Render segments
  return (
    <>
      {segments.map((segment, idx) => {
        if (segment.type === 'string') {
          return <span key={idx} style={{ color: isDarkMode ? '#10b981' : '#198754' }}>{segment.text}</span>;
        } else {
          // Highlight keywords in code segments
          const keywordParts = segment.text.split(keywords);
          return keywordParts.map((part, partIdx) => {
            if (keywords.test(part)) {
              return <span key={`${idx}-${partIdx}`} style={{ color: isDarkMode ? '#8b5cf6' : '#6f42c1', fontWeight: '600' }}>{part}</span>;
            }
            return <span key={`${idx}-${partIdx}`}>{part}</span>;
          });
        }
      })}
    </>
  );
};

// Helper component for source tab content
const SourceTabContent = ({ source, isSQLDatatype, datatype, id, curl_commands, isStreaming }) => {
  const { isDarkMode } = useDarkMode();
  const hasCurlCommands = curl_commands && curl_commands.length > 0;
  const hasSQL = (isSQLDatatype || datatype === "insights") && source;

  // Show both SQL and cURL if available
  if (hasSQL || hasCurlCommands) {
    return (
      <div>
        {/* Show SQL if available */}
        {hasSQL && (
          <div className={hasCurlCommands ? "mb-4" : ""}>
            <h5 className={`mb-2 font-semibold ${
              isDarkMode ? 'text-slate-100' : 'text-gray-900'
            }`}>Executed SQL:</h5>
            <div className="font-inherit text-inherit whitespace-pre-wrap">
              {Array.isArray(source) ? (
                  source.map((item, index) => {
                    // Check if item is string or object
                    const queryContent = typeof item === 'string' ? item : (item.query || JSON.stringify(item));
                    
                    // Split by newlines and render each line separately
                    const lines = queryContent.split('\n');
                    
                    return (
                      <div key={index} className="mb-3">
                        <div className={`rounded-lg p-3 overflow-x-auto table-scrollbar font-mono text-sm leading-relaxed ${
                          isDarkMode ? 'bg-slate-900 text-slate-200' : 'bg-gray-50 text-gray-900'
                        }`}>
                          {lines.map((line, lineIndex) => (
                            <div key={lineIndex} className="min-h-[1.7em]">
                              {highlightSQL(line, isDarkMode)}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={`rounded-lg p-3 overflow-x-auto table-scrollbar font-mono text-sm leading-relaxed ${
                    isDarkMode ? 'bg-slate-900 text-slate-200' : 'bg-gray-50 text-gray-900'
                  }`}>
                    {(typeof source === "string" ? source : JSON.stringify(source)).split('\n').map((line, lineIndex) => (
                      <div key={lineIndex} className="min-h-[1.7em]">
                        {highlightSQL(line, isDarkMode)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
          </div>
        )}

        {/* Show cURL commands if available */}
        {hasCurlCommands && (
            <div>
              <h5 className={`mb-2 font-semibold ${
                isDarkMode ? 'text-slate-100' : 'text-gray-900'
              }`}>API Calls:</h5>
              {curl_commands.map((cmd, index) => {
                const curlContent = cmd.curl || "No curl command available";
                const lines = curlContent.split('\n');
                
                return (
                  <div key={index} className="mb-3">
                    {cmd.url && (
                      <p className={`mb-2 ${
                        isDarkMode ? 'text-slate-200' : 'text-gray-900'
                      }`}>
                        <strong className={isDarkMode ? 'text-slate-100' : 'text-gray-900'}>URL:</strong> {cmd.url}
                      </p>
                    )}
                    <div className={`rounded-lg p-3 overflow-x-auto table-scrollbar font-mono text-sm leading-relaxed ${
                      isDarkMode ? 'bg-slate-900 text-slate-200' : 'bg-gray-50 text-gray-900'
                    }`}>
                      {lines.map((line, lineIndex) => (
                        <div key={lineIndex} className="min-h-[1.7em]">
                          {highlightBash(line, isDarkMode)}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    );
  }

  if (datatype === "documents") {
    return (
      <div className="input-group">
        {source.length > 0 ? (
          source.map((msg, index) => <SourceCard key={`${id}-${index}`} msg={msg} chatMessageId={id} />)
        ) : isStreaming ? (
          <span className={isDarkMode ? 'text-slate-200' : 'text-gray-900'}>Sources are being generated…</span>
        ) : (
          <>No Source to display.</>
        )}
      </div>
    );
  }

  if (datatype === "error") {
    return <>No Source to display.</>;
  }

  if (isStreaming) {
    return <span className={isDarkMode ? 'text-slate-200' : 'text-gray-900'}>Sources are being generated…</span>;
  }

  return <>No Source to display.</>;
};

SourceTabContent.propTypes = {
  source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isSQLDatatype: PropTypes.bool.isRequired,
  datatype: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  curl_commands: PropTypes.array,
  isStreaming: PropTypes.bool,
};

// Helper component for description tab content
const DescriptionTabContent = ({ description, message, isSQLDatatype, datatype, source, id, isTable, containsMarkdown, handleSend, isAuthorAdmin, clarificationOptions, handleClarificationOptionClick, clarificationOptionsDisabled, selectedClarificationOption, setPaginationState, isProcessing, processingContent, isStreaming, onChunkClick }) => {
  const { isDarkMode } = useDarkMode();

  // If processing, show the same loading indicator as in Data tab using centralized processingContent
  if (isProcessing) {
    return (
      <div className={`whitespace-pre-wrap break-words ${
        isDarkMode ? 'text-slate-200' : 'text-gray-900'
      }`}>
        <MarkdownRenderer content={processingContent} />
      </div>
    );
  }

  // Render HTML descriptions for any datatype
  if (typeof description === 'string' && description.trimStart().startsWith('<')) {
    return (
      <div
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }}
        onClick={(e) => {
          const a = e.target.closest('a[href^="#cite-"]');
          if (a && onChunkClick) {
            e.preventDefault();
            onChunkClick(parseInt(a.getAttribute('href').slice(6), 10));
          }
        }}
      />
    );
  }

  // For insights datatype, show the description as markdown
  if (datatype === "insights") {
    if (description) {
      return <MarkdownRenderer content={description} onChunkClick={onChunkClick} />;
    }
    if (isStreaming) return <span className={isDarkMode ? 'text-slate-200' : 'text-gray-900'}>Insights are being generated…</span>;
    return <span className={isDarkMode ? 'text-slate-200' : 'text-gray-900'}>No description available</span>;
  }

  // For SQL datatype, the message is table/records data — never render it in the Insights tab.
  // Show description if present, otherwise a friendly placeholder.
  if (isSQLDatatype) {
    if (description) return <MarkdownRenderer content={description} onChunkClick={onChunkClick} />;
    if (isStreaming) return <span className={isDarkMode ? 'text-slate-200' : 'text-gray-900'}>Insights are being generated…</span>;
    return <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>No description available</span>;
  }

  // For text datatype or when there's a description, show it
  if (description) {
    return <MarkdownRenderer content={description} onChunkClick={onChunkClick} />;
  }

  // Otherwise show the message content
  return (
    <MessageContent
      message={message}
      isTable={isTable}
      containsMarkdown={containsMarkdown}
      id={id}
      handleSend={handleSend}
      isAuthorAdmin={isAuthorAdmin}
      clarificationOptions={clarificationOptions}
      handleClarificationOptionClick={handleClarificationOptionClick}
      clarificationOptionsDisabled={clarificationOptionsDisabled}
      selectedClarificationOption={selectedClarificationOption}
      setPaginationState={setPaginationState}
      onChunkClick={onChunkClick}
    />
  );
};

DescriptionTabContent.propTypes = {
  description: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
  isSQLDatatype: PropTypes.bool.isRequired,
  datatype: PropTypes.string,
  source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isTable: PropTypes.func.isRequired,
  containsMarkdown: PropTypes.func.isRequired,
  handleSend: PropTypes.func.isRequired,
  isAuthorAdmin: PropTypes.bool.isRequired,
  clarificationOptions: PropTypes.array,
  handleClarificationOptionClick: PropTypes.func,
  clarificationOptionsDisabled: PropTypes.bool,
  selectedClarificationOption: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  setPaginationState: PropTypes.func,
  isProcessing: PropTypes.bool,
  processingContent: PropTypes.string,
  isStreaming: PropTypes.bool,
  onChunkClick: PropTypes.func,
};

/**
 * Renders content directly without tabs — used for rag, clarification, and unknown answer types.
 * For rag: renders the insights/description content (markdown or HTML).
 * For clarification/unknown: renders message content with clarification options.
 */
const DirectContent = ({
  answer_type,
  analysisMode,
  message,
  description,
  dataSourcesMap,
  replaceCitationMarkers,
  isTable,
  containsMarkdown,
  id,
  handleSend,
  isAuthorAdmin,
  clarificationOptions,
  handleClarificationOptionClick,
  clarificationOptionsDisabled,
  selectedClarificationOption,
  setPaginationState,
  isStreaming,
  onChunkClick,
}) => {
  const { isDarkMode } = useDarkMode();

  if (answer_type === 'rag') {
    const processCitations = (text) => replaceCitationMarkers ? replaceCitationMarkers(text) : text;

    // Renders the curated insights (key_insights + actionable_recommendations,
    // carried in `description`) as HTML or markdown. Returns null when there's none.
    const renderInsights = () => {
      if (!description) return null;
      if (typeof description === 'string' && description.trimStart().startsWith('<')) {
        return <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }} />;
      }
      return <MarkdownRenderer content={description} onChunkClick={onChunkClick} />;
    };

    // Renders a single data_sources value: raw HTML, markdown with citations,
    // or JSON-stringified markdown for non-string values.
    const renderDataSourceValue = (val) => {
      if (typeof val !== 'string') {
        return <MarkdownRenderer content={JSON.stringify(val)} onChunkClick={onChunkClick} />;
      }
      if (val.trimStart().startsWith('<')) {
        return <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(val) }} />;
      }
      return <MarkdownRenderer content={processCitations(val)} onChunkClick={onChunkClick} />;
    };

    // Renders the raw data_sources values directly, with citations processed.
    // Returns null when there are no data sources to show.
    const renderDataSources = () => {
      if (!dataSourcesMap || typeof dataSourcesMap !== 'object') return null;
      const entries = Object.entries(dataSourcesMap);
      if (entries.length === 0) return null;
      return (
        <div>
          {entries.map(([key, val]) => (
            <div key={key}>{renderDataSourceValue(val)}</div>
          ))}
        </div>
      );
    };

    // Deep ("thinking") mode produces key_insights + actionable_recommendations —
    // always show those, never the raw retrieved chunks. Other modes are RAG
    // passthroughs where data_sources IS the answer, so show that first and only
    // fall back to insights when there are no data sources.
    const insights = renderInsights();
    const sources = renderDataSources();
    const primary = analysisMode === 'deep'
      ? (insights || sources)
      : (sources || insights);
    if (primary) return primary;

    if (message) {
      return <MarkdownRenderer content={typeof message === 'string' ? message : JSON.stringify(message)} onChunkClick={onChunkClick} />;
    }
    return <span className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>No content available</span>;
  }

  // unknown answer with no message (e.g. answer_summary content, empty key_insights):
  // fall back to description instead of showing a blank bubble.
  const hasMessage = typeof message === 'string' ? message.trim() : message;
  if (!hasMessage && description && !clarificationOptions?.length) {
    if (typeof description === 'string' && description.trimStart().startsWith('<')) {
      return <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }} />;
    }
    return <MarkdownRenderer content={description} onChunkClick={onChunkClick} />;
  }

  // clarification / unknown: render message content with clarification options
  return (
    <MessageContent
      message={message}
      isTable={isTable}
      containsMarkdown={containsMarkdown}
      id={id}
      handleSend={handleSend}
      isAuthorAdmin={isAuthorAdmin}
      clarificationOptions={clarificationOptions}
      handleClarificationOptionClick={handleClarificationOptionClick}
      clarificationOptionsDisabled={clarificationOptionsDisabled}
      selectedClarificationOption={selectedClarificationOption}
      setPaginationState={setPaginationState}
      onChunkClick={onChunkClick}
    />
  );
};

DirectContent.propTypes = {
  answer_type: PropTypes.string,
  analysisMode: PropTypes.oneOf(["deep", "fast"]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  description: PropTypes.string,
  dataSourcesMap: PropTypes.object,
  replaceCitationMarkers: PropTypes.func,
  isTable: PropTypes.func,
  containsMarkdown: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleSend: PropTypes.func.isRequired,
  isAuthorAdmin: PropTypes.bool.isRequired,
  clarificationOptions: PropTypes.array,
  handleClarificationOptionClick: PropTypes.func,
  clarificationOptionsDisabled: PropTypes.bool,
  selectedClarificationOption: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  setPaginationState: PropTypes.func,
  isStreaming: PropTypes.bool,
  onChunkClick: PropTypes.func,
};

// Helper component for feedback modal
const FeedbackModal = ({ showModal, feedbackComment, setFeedbackComment, handleModalSubmit, handleModalClose, isSubmitting }) => {
  const { isDarkMode } = useDarkMode();
  const isValid = feedbackComment.trim().length >= 1;
  
  if (!showModal) return null;
  
  return (
    <>
      {/* Backdrop with blur */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] transition-opacity"
        onClick={handleModalClose}
      />
      
      {/* Modal - Increased width to match reference */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div 
          className={`relative w-full max-w-lg rounded-xl shadow-2xl ${
            isDarkMode ? 'bg-slate-800' : 'bg-white'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleModalClose}
            disabled={isSubmitting}
            className={`absolute top-4 right-4 p-1 rounded-lg transition-colors ${
              isDarkMode 
                ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200' 
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${
                isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'
              }`}>
                {/* Thumbs down icon instead of message bubble */}
                <ThumbsDown className={`w-5 h-5 ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-semibold mb-1 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Tell us what went wrong
                </h3>
                <p className={`text-sm ${
                  isDarkMode ? 'text-slate-400' : 'text-gray-600'
                }`}>
                  Your feedback helps us improve response quality.
                </p>
              </div>
            </div>
          </div>
          
          {/* Body */}
          <div className="px-6 pb-4">
            <textarea
              rows={4}
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              placeholder="What didn't work as expected?"
              disabled={isSubmitting}
              className={`w-full rounded-lg px-4 py-3 border-2 focus:outline-none focus:ring-0 transition-colors resize-none custom-scrollbar ${
                isDarkMode 
                  ? 'bg-slate-700/50 border-slate-600 text-white placeholder-slate-500 focus:border-blue-500 focus:bg-slate-700' 
                  : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white'
              } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            />
          </div>
          
          {/* Footer */}
          <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t ${
            isDarkMode ? 'border-slate-700' : 'border-gray-200'
          }`}>
            <button
              onClick={handleModalClose}
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                isDarkMode 
                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Cancel
            </button>
            <button
              onClick={handleModalSubmit}
              disabled={!isValid || isSubmitting}
              className={`px-5 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                isValid && !isSubmitting
                  ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md'
                  : 'bg-teal-600 text-white cursor-not-allowed opacity-60'
              }`}
            >
              {isSubmitting && <Spinner className="h-4 w-4" />}
              {isSubmitting ? 'Sending...' : 'Send feedback'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

FeedbackModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  feedbackComment: PropTypes.string.isRequired,
  setFeedbackComment: PropTypes.func.isRequired,
  handleModalSubmit: PropTypes.func.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

// Helper component for action buttons
const CostModal = ({ costData, costError, onClose, isDarkMode }) => {
  // Which call row's details are currently expanded — null = all collapsed.
  // Per-row toggle matches the old per-component UX users were used to and
  // avoids the visual crowding of showing tokens + model on every row at once.
  const [expandedCallId, setExpandedCallId] = useState(null);

  const formatCost = (val) => {
    if (val == null) return '-';
    const num = Number(val);
    return isNaN(num) ? '-' : `$${num.toFixed(6)}`;
  };

  const formatMs = (val) => {
    if (val == null) return '-';
    const ms = parseInt(val, 10);
    if (isNaN(ms)) return val;
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${ms}ms`;
  };

  // Render the producer's started_at (ISO 8601 UTC) as HH:MM:SS in the
  // user's local timezone. Falls back to em-dash when the row pre-dates the
  // started_at column rollout (NULL → string '' from json normalization).
  // Sub-second precision dropped for display readability — backend ORDER BY
  // still uses microsecond precision so close-together calls retain correct
  // ordering even when their displayed timestamps collide.
  const formatStartedAt = (iso) => {
    if (!iso) return '—';
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return '—';
    const hh = String(dt.getHours()).padStart(2, '0');
    const mm = String(dt.getMinutes()).padStart(2, '0');
    const ss = String(dt.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  const d = isDarkMode;
  const bg = d ? 'bg-slate-900' : 'bg-white';
  const border = d ? 'border-slate-700/60' : 'border-gray-200';
  const text = d ? 'text-slate-100' : 'text-gray-900';
  const sub = d ? 'text-slate-400' : 'text-gray-500';
  const cardBg = d ? 'bg-slate-800/70' : 'bg-gray-50';
  const hoverRow = d ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50';
  const expandBg = d ? 'bg-slate-800/40' : 'bg-gray-50/80';

  const TOKEN_TYPES = [
    { key: ['input_tokens', 'input'],     label: 'Input',     color: d ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-blue-50 text-blue-600 border-blue-200' },
    { key: ['output_tokens', 'output'],   label: 'Output',    color: d ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    { key: ['thinking_tokens', 'thinking'], label: 'Thinking', color: d ? 'bg-orange-500/20 text-orange-300 border-orange-500/30' : 'bg-orange-50 text-orange-600 border-orange-200' },
    { key: ['cached_tokens', 'cached'],         label: 'Cached',         color: d ? 'bg-purple-500/20 text-purple-300 border-purple-500/30' : 'bg-purple-50 text-purple-600 border-purple-200' },
    { key: ['cache_storage_tokens'],             label: 'Cache Storage',  color: d ? 'bg-teal-500/20 text-teal-300 border-teal-500/30' : 'bg-teal-50 text-teal-600 border-teal-200' },
    { key: ['embedding_tokens', 'embedding'],    label: 'Embedding',      color: d ? 'bg-pink-500/20 text-pink-300 border-pink-500/30' : 'bg-pink-50 text-pink-600 border-pink-200' },
  ];

  const getToken = (breakdown, keys) => {
    for (const k of keys) if (breakdown?.[k] != null) return breakdown[k];
    return null;
  };

  const TokenRow = ({ breakdown }) => {
    if (!breakdown) return null;
    const tokens = TOKEN_TYPES.map(t => ({ ...t, val: getToken(breakdown, t.key) })).filter(t => t.val != null && t.val > 0);
    if (!tokens.length) return null;
    return (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {tokens.map(t => (
          <span key={t.label} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md border font-mono ${t.color}`}>
            <span className={`text-[10px] font-sans opacity-70`}>{t.label}</span>
            {t.val.toLocaleString()}
          </span>
        ))}
      </div>
    );
  };

  const StatCard = ({ label, value, valueClass }) => (
    <div className={`flex flex-col gap-0.5 px-4 py-3 rounded-xl ${cardBg}`}>
      <span className={`text-[11px] font-medium uppercase tracking-wider ${sub}`}>{label}</span>
      <span className={`text-sm font-bold ${valueClass || text}`}>{value}</span>
    </div>
  );

  // Op-type badge palette — matches the existing TOKEN_TYPES color scheme:
  //   LLM=blue, EMB=pink, CACHE=teal, SQL=emerald, API=amber.
  // Each call row shows one of these as a small leading pill so users can
  // scan op-types at a glance in the chronological waterfall.
  const OP_BADGE = {
    llm_call: {
      label: 'LLM',
      cls: d
        ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
        : 'bg-blue-50 text-blue-600 border-blue-200',
      Icon: MessageSquare,
    },
    embedding_call: {
      label: 'EMB',
      cls: d
        ? 'bg-pink-500/20 text-pink-300 border-pink-500/30'
        : 'bg-pink-50 text-pink-600 border-pink-200',
      Icon: Hash,
    },
    cache_storage: {
      label: 'CACHE CREATED',
      cls: d
        ? 'bg-teal-500/20 text-teal-300 border-teal-500/30'
        : 'bg-teal-50 text-teal-600 border-teal-200',
      Icon: HardDrive,
    },
    sql_query: {
      label: 'SQL',
      cls: d
        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
        : 'bg-emerald-50 text-emerald-700 border-emerald-200',
      Icon: Database,
    },
    api_call: {
      label: 'API',
      cls: d
        ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
        : 'bg-amber-50 text-amber-700 border-amber-200',
      Icon: Globe,
    },
    // Fallback for unknown / future op_types. Prefer surfacing the raw
    // operationType in the label (uppercased) so users see WHAT it is
    // rather than silently mislabeling it as LLM during a rollout window
    // where a new producer op_type ships ahead of the chat-UI deploy.
    unknown: {
      label: 'UNKNOWN',
      cls: d
        ? 'bg-slate-500/20 text-slate-300 border-slate-500/30'
        : 'bg-slate-100 text-slate-600 border-slate-300',
      Icon: HelpCircle,
    },
  };

  // Render the cost badge for a single call row — color matches OP_BADGE so
  // the cost and op-type read as the same thing visually.
  //
  // NOTE: this is a plain helper function (call via `renderCallCostBadge(call)`)
  // — NOT a component. Defining it as a component (`<CallCostBadge ... />`)
  // inside CostModal would mean React sees a fresh component type on every
  // render (`expandedCallId` flips), causing every row to unmount + remount
  // and lose DOM state. Plain helpers don't trigger that; they're just inline
  // JSX returned from a function call.
  //
  // Fallback handling: for ops outside the (llm / embedding / cache_storage)
  // colorMap that still carry a costUsd (future op types, or sql_query /
  // api_call if they ever get priced), render a neutral cost badge so the
  // value is surfaced rather than silently dropped.
  const renderCallCostBadge = (call) => {
    if (call.costUsd == null) return null;
    const colorMap = {
      llm_call: d ? 'text-blue-300' : 'text-blue-600',
      embedding_call: d ? 'text-pink-300' : 'text-pink-600',
      cache_storage: d ? 'text-teal-300' : 'text-teal-600',
    };
    const labelMap = {
      llm_call: 'LLM',
      embedding_call: 'EMB',
      cache_storage: 'CACHE',
    };
    const cls = colorMap[call.operationType] ?? (d ? 'text-slate-300' : 'text-slate-600');
    const lbl =
      labelMap[call.operationType] ??
      (call.operationType
        ? call.operationType.toUpperCase().replace(/_/g, ' ')
        : 'COST');
    return (
      <span className={`text-xs font-semibold tabular-nums ${cls}`}>
        {lbl} {formatCost(call.costUsd)}
      </span>
    );
  };

  // Render the per-call token-chip row — same color palette as TokenRow but
  // reads the flat token fields (inputTokens, outputTokens, etc.) directly
  // off a CallEntry. cache_storage rows route cachedTokens (= cache size) to
  // the teal cache_storage chip; everything else routes it to the purple
  // cached chip (= prompt-cache hit tokens).
  //
  // NOTE: plain helper (not a component) for the same React-identity reason
  // documented on renderCallCostBadge.
  const renderCallTokenChips = (call) => {
    const isCacheStorage = call.operationType === 'cache_storage';
    const chips = [
      { label: 'Input', val: call.inputTokens, color: TOKEN_TYPES[0].color },
      { label: 'Output', val: call.outputTokens, color: TOKEN_TYPES[1].color },
      { label: 'Thinking', val: call.thinkingTokens, color: TOKEN_TYPES[2].color },
      {
        label: 'Cached',
        val: isCacheStorage ? null : call.cachedTokens,
        color: TOKEN_TYPES[3].color,
      },
      {
        label: 'Cache Storage',
        val: isCacheStorage ? call.cachedTokens : null,
        color: TOKEN_TYPES[4].color,
      },
      {
        label: 'Embedding',
        val: call.embeddingTokens,
        color: TOKEN_TYPES[5].color,
      },
    ].filter((c) => c.val != null && c.val > 0);
    if (!chips.length) return null;
    return (
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {chips.map((c) => (
          <span
            key={c.label}
            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border font-mono ${c.color}`}
          >
            <span className="text-[10px] font-sans opacity-70">{c.label}</span>
            {c.val.toLocaleString()}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border shadow-2xl ${bg} ${border}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-3.5 border-b ${border} shrink-0`}>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${d ? 'bg-green-500/20' : 'bg-green-50'}`}>
              <DollarSign className={`w-4 h-4 ${d ? 'text-green-400' : 'text-green-600'}`} />
            </div>
            <h2 className={`text-sm font-semibold ${text}`}>Cost Breakdown</h2>
          </div>
          <button onClick={onClose} className={`p-1.5 rounded-lg transition-colors ${d ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          {costError ? (
            <div className={`flex items-center gap-2 p-3 rounded-xl ${d ? 'bg-red-900/30 border border-red-700/40 text-red-300' : 'bg-red-50 border border-red-200 text-red-600'} text-sm`}>
              {costError}
            </div>
          ) : !costData ? null : (
            <>
              {/* Total cost hero */}
              <div className={`p-4 rounded-xl ${d ? 'bg-gradient-to-br from-green-900/40 to-emerald-900/20 border border-green-700/30' : 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'}`}>
                <p className={`text-xs font-medium uppercase tracking-wider mb-1 ${d ? 'text-green-400/70' : 'text-green-600/70'}`}>Total Cost</p>
                <p className={`text-2xl font-bold ${d ? 'text-green-300' : 'text-green-700'}`}>
                   {formatCost(costData.totalCost ?? ((costData.llmCost ?? 0) + (costData.embeddingCost ?? 0) + (costData.cacheStorageCost ?? 0)))}
                </p>
              </div>

              {/* Stat cards */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <StatCard label="LLM Cost" value={formatCost(costData.llmCost)} valueClass={d ? 'text-blue-300' : 'text-blue-600'} />
                {costData.embeddingCost > 0 && <StatCard label="Embedding" value={formatCost(costData.embeddingCost)} valueClass={d ? 'text-pink-300' : 'text-pink-600'} />}
                {costData.cacheStorageCost > 0 && <StatCard label="Cache Storage" value={formatCost(costData.cacheStorageCost)} valueClass={d ? 'text-teal-300' : 'text-teal-600'} />}
                <StatCard label="Duration" value={formatMs(costData.duration)} />
                {costData.llmCalls != null && <StatCard label="LLM Calls" value={costData.llmCalls} />}
              </div>

              {/* Total token breakdown */}
              {costData.breakdown && (
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${sub}`}>Total Tokens</p>
                  <TokenRow breakdown={costData.breakdown} />
                </div>
              )}

              {/* Session Title (one call row emitted by the post-answer
                  background label generation. Backend extracts it from the
                  flat call list and returns it as a single CallEntry). */}
              {costData.sessionTitle && (
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${sub}`}>Session</p>
                  <div className={`px-4 py-3 rounded-xl border ${border} ${cardBg}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${d ? 'bg-slate-700 text-slate-300' : 'bg-gray-200 text-gray-600'}`}>
                          Session Created
                        </span>
                        {costData.sessionTitle.modelName && (
                          <span className={`text-xs ${sub} font-mono`}>{costData.sessionTitle.modelName}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs ${sub}`}>{formatMs(costData.sessionTitle.duration)}</span>
                        <span className={`text-xs font-semibold ${d ? 'text-blue-300' : 'text-blue-600'}`}>{formatCost(costData.sessionTitle.costUsd)}</span>
                      </div>
                    </div>
                    {renderCallTokenChips(costData.sessionTitle)}
                  </div>
                </div>
              )}

              {/* Call timeline — flat chronological waterfall of every
                  observability event (llm_call / embedding_call /
                  cache_storage / sql_query / api_call), ordered by
                  started_at. Backend filters session_title rows into the
                  separate sessionTitle field above.

                  Each row collapsed by default — clicking expands to show
                  token chips + model + extras. Same UX pattern as the
                  previous per-component card; just one card per call
                  instead of one per (agent, component) tuple. */}
              {costData.details && costData.details.length > 0 && (
                <div>
                  <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${sub}`}>Call Timeline</p>
                  <div className="space-y-1.5">
                    {costData.details.map((call) => {
                      // Default to UNKNOWN (not llm_call) so a producer-side
                      // op_type added before the chat-UI ships an update
                      // surfaces as 'UNKNOWN' rather than mislabeling as LLM.
                      const badge = OP_BADGE[call.operationType] ?? OP_BADGE.unknown;
                      const BadgeIcon = badge.Icon;
                      const isError = call.status && call.status !== 'success';
                      const isExpanded = expandedCallId === call.id;
                      const rowBorder = isError
                        ? (d ? 'border-red-700/40' : 'border-red-300')
                        : border;
                      return (
                        <div
                          key={call.id}
                          className={`rounded-xl border ${rowBorder} overflow-hidden`}
                        >
                          {/* Collapsed view — kept minimal so the timeline
                              stays scannable. Op-type badge + token chips +
                              model live in the dropdown (above) — the cost
                              color already telegraphs op-type (LLM=blue,
                              EMB=pink, CACHE=teal); SQL / API have no cost so
                              expanding is how the user confirms what they
                              are. */}
                          <button
                            type="button"
                            aria-expanded={isExpanded}
                            className={`w-full flex items-center justify-between px-3 py-2.5 transition-colors ${hoverRow}`}
                            onClick={() =>
                              setExpandedCallId(isExpanded ? null : call.id)
                            }
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className={`text-[11px] font-mono tabular-nums shrink-0 ${sub}`}>
                                {formatStartedAt(call.startedAt)}
                              </span>
                              {call.agentName && (
                                <span className={`text-xs px-2 py-0.5 rounded-md font-medium shrink-0 ${d ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                                  {call.agentName}
                                </span>
                              )}
                              {call.componentName && (
                                <span className={`text-sm font-medium truncate ${text}`}>{call.componentName}</span>
                              )}
                              {/* callLabel: human-readable name for the
                                  semantic LLM call. Rendered as muted
                                  inline text after the component name with
                                  a "·" separator (NOT as a pill) — natural
                                  read order is "what (component) is doing
                                  what (label)", and a third colored pill
                                  after the agent pill would crowd the
                                  narrow chat-modal row. Indigo color keeps
                                  it visually distinct from the gray
                                  component name without competing with the
                                  amber agent pill for attention.

                                  Separator is conditional on componentName
                                  being present — without it, a leading
                                  "·" would orphan in front of the label
                                  text. In that case the label renders
                                  naked. */}
                              {call.callLabel && (
                                <>
                                  {call.componentName && (
                                    <span className={`text-xs ${sub}`}>·</span>
                                  )}
                                  <span className={`text-xs font-medium truncate ${d ? 'text-indigo-300' : 'text-indigo-600'}`}>
                                    {call.callLabel}
                                  </span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-2.5 shrink-0 ml-3">
                              <span className={`text-xs tabular-nums ${sub}`}>{formatMs(call.duration)}</span>
                              {renderCallCostBadge(call)}
                              {isExpanded ? (
                                <ChevronUp className={`w-3.5 h-3.5 ${sub}`} />
                              ) : (
                                <ChevronDown className={`w-3.5 h-3.5 ${sub}`} />
                              )}
                            </div>
                          </button>
                          {isExpanded && (
                            <div className={`px-3 pb-3 pt-2 border-t ${border} ${expandBg} space-y-2`}>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md border ${badge.cls}`}>
                                  <BadgeIcon className="w-3 h-3" />
                                  {badge.label}
                                </span>
                                {call.modelName && (
                                  <p className={`text-xs ${sub}`}>
                                    Model: <span className={`font-mono ${text}`}>{call.modelName}</span>
                                  </p>
                                )}
                                {call.operationType === 'cache_storage' && call.cacheTtlSeconds != null && (
                                  <p className={`text-xs ${sub}`}>
                                    TTL: <span className={`font-medium ${text}`}>{call.cacheTtlSeconds}s</span>
                                  </p>
                                )}
                                {call.operationType === 'cache_storage' && call.cachedTokens != null && (
                                  <p className={`text-xs ${sub}`}>
                                    Size: <span className={`font-medium ${text}`}>{call.cachedTokens.toLocaleString()} tokens</span>
                                  </p>
                                )}
                              </div>
                              {renderCallTokenChips(call)}
                              {isError && call.errorMessage && (
                                <p className={`text-[11px] ${d ? 'text-red-300' : 'text-red-600'}`}>
                                  {call.errorMessage}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

CostModal.propTypes = {
  costData: PropTypes.object,
  costError: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  isDarkMode: PropTypes.bool,
};

const ActionButtons = ({
  selectedTab,
  isSQLDatatype,
  source,
  message,
  description,
  dataSourcesMap,
  curl_commands,
  onReply,
  isAuthorAdmin,
  feedback,
  handleFeedback,
  handleDislikeClick,
  id,
  chatSessionId,
  paginationState,
  isStreaming,
  activeSourceKey,
  isMultiSource,
  onShowCost,
  costLoading,
}) => {
  const { isDarkMode } = useDarkMode();
  
  return (
    <div className={`flex items-center justify-between px-4 py-2 border-t ${
      isDarkMode ? 'border-slate-700' : 'border-gray-100'
    }`}>
      {/* Left side: Action buttons */}
      <div className="flex items-center gap-1">
        <CopyButton
          selectedTab={selectedTab}
          copyText={message}
          source={source}
          curl_commands={curl_commands}
          description={description}
          dataSourcesMap={dataSourcesMap}
        />
        {isAuthorAdmin && (
          <>
            {/* Reply button temporarily hidden */}
            {/* <Tooltip text="Reply" isDarkMode={isDarkMode}>
              <button 
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
                onClick={onReply}
              >
                <Reply className="w-4 h-4" />
              </button>
            </Tooltip> */}
            <Tooltip text="Like" isDarkMode={isDarkMode}>
              <button 
                className={`p-2 rounded-lg transition-colors ${
                  feedback === 1 
                    ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                    : (isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700')
                }`}
                onClick={() => handleFeedback(1)}
              >
                <ThumbsUp className={`w-4 h-4 ${feedback === 1 ? 'fill-current' : ''}`} />
              </button>
            </Tooltip>
            <Tooltip text="Dislike" isDarkMode={isDarkMode}>
              <button
                className={`p-2 rounded-lg transition-colors ${
                  feedback === -1
                    ? (isDarkMode ? 'text-red-400' : 'text-red-600')
                    : (isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-slate-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700')
                }`}
                onClick={handleDislikeClick}
              >
                <ThumbsDown className={`w-4 h-4 ${feedback === -1 ? 'fill-current' : ''}`} />
              </button>
            </Tooltip>
            <Tooltip text="View Cost" isDarkMode={isDarkMode}>
              <button
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-slate-700 text-slate-400 hover:text-green-400' : 'hover:bg-gray-100 text-gray-500 hover:text-green-600'
                } ${costLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={onShowCost}
                disabled={costLoading || isStreaming}
              >
                {costLoading
                  ? <span className="w-4 h-4 block rounded-full border-2 border-current border-t-transparent animate-spin" />
                  : <DollarSign className="w-4 h-4" />
                }
              </button>
            </Tooltip>
          </>
        )}
        {isAuthorAdmin && (
          <Options
            id={id}
            sessionId={chatSessionId}
            sourceKey={activeSourceKey || undefined}
            isMultiSource={!!isMultiSource}
            tooltipText="Download"
            disabled={selectedTab !== 'data' || isStreaming}
          />
        )}
      </div>

      {/* Right side: Pagination controls (only for client-side pagination, not server-side) */}
      {paginationState && paginationState.showPagination && !paginationState.isServerSide && selectedTab === 'data' && (
        <div className="flex items-center gap-4">
          {/* Loading indicator and record count */}
          <div className="flex items-center gap-2">
            {paginationState.isLoading && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle 
                  className={isDarkMode ? 'opacity-25 stroke-slate-400' : 'opacity-25 stroke-gray-400'} 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  strokeWidth="4"
                />
                <path 
                  className={isDarkMode ? 'opacity-75 fill-slate-200' : 'opacity-75 fill-gray-600'} 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            <span className={`text-sm ${
              isDarkMode ? 'text-slate-400' : 'text-gray-600'
            }`}>
              {paginationState.startRecord}-{paginationState.endRecord} of {paginationState.totalRows}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Previous button */}
            <button
              onClick={() => paginationState.handlePageChange(paginationState.pageNum - 1)}
              disabled={paginationState.pageNum === 1 || paginationState.isLoading}
              title="Previous page"
              className={`p-1 rounded-md transition-colors ${
                paginationState.pageNum === 1 || paginationState.isLoading
                  ? (isDarkMode 
                      ? 'text-slate-600 cursor-not-allowed' 
                      : 'text-gray-400 cursor-not-allowed')
                  : (isDarkMode 
                      ? 'text-slate-400 hover:bg-slate-700 hover:text-white' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900')
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Next button */}
            <button
              onClick={() => paginationState.handlePageChange(paginationState.pageNum + 1)}
              disabled={paginationState.pageNum === paginationState.totalPages || paginationState.isLoading}
              title="Next page"
              className={`p-1 rounded-md transition-colors ${
                paginationState.pageNum === paginationState.totalPages || paginationState.isLoading
                  ? (isDarkMode 
                      ? 'text-slate-600 cursor-not-allowed' 
                      : 'text-gray-400 cursor-not-allowed')
                  : (isDarkMode 
                      ? 'text-slate-400 hover:bg-slate-700 hover:text-white' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900')
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

ActionButtons.propTypes = {
  selectedTab: PropTypes.string.isRequired,
  isSQLDatatype: PropTypes.bool.isRequired,
  source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
  description: PropTypes.string,
  dataSourcesMap: PropTypes.object,
  curl_commands: PropTypes.array,
  onReply: PropTypes.func.isRequired,
  isAuthorAdmin: PropTypes.bool.isRequired,
  feedback: PropTypes.number.isRequired,
  handleFeedback: PropTypes.func.isRequired,
  handleDislikeClick: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  chatSessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  paginationState: PropTypes.shape({
    isLoading: PropTypes.bool,
    pageNum: PropTypes.number,
    totalPages: PropTypes.number,
    startRecord: PropTypes.number,
    endRecord: PropTypes.number,
    totalRows: PropTypes.number,
    handlePageChange: PropTypes.func,
    showPagination: PropTypes.bool,
    isServerSide: PropTypes.bool,
  }),
  isStreaming: PropTypes.bool,
  activeSourceKey: PropTypes.string,
  isMultiSource: PropTypes.bool,
  onShowCost: PropTypes.func,
  costLoading: PropTypes.bool,
};

const ChatMessage = ({
  id,
  authorId,
  author,
  avatar,
  message,
  source,
  description,
  totalRows,
  dataSourcesMap,
  datatype,
  parentMessage,
  resizeInput,
  setQuotedMessage,
  setCurrentParentId,
  chatSessionId,
  setLiveChat,
  clarificationOptions = [],
  onClarificationOptionClick,
  clarificationOptionsDisabled = false,
  selectedClarificationOption = null,
  curl_commands = [],
  initialFeedback = 0,
  timestamp,
  isStreaming = false,
  analysis_mode,
  reference,
  answer_type,
  reasoning_steps = [],
}) => {
  const { config } = useAgentConfig();
  const { isDarkMode } = useDarkMode();
  const [selectedTab, setSelectedTab] = useState("data");
  const messageContainerRef = useRef(null);
  const tabChangeAnchorRef = useRef(null); // { containerTop } snapshot before tab switch
  const hasUserSelectedTab = useRef(false);

  const handleTabChange = (tab) => {
    hasUserSelectedTab.current = true;
    if (messageContainerRef.current) {
      tabChangeAnchorRef.current = {
        containerTop: messageContainerRef.current.getBoundingClientRect().top,
      };
    }
    // Suppress native scroll anchoring only for the duration of this tab switch
    // so the browser doesn't interfere with our manual ResizeObserver compensation.
    const scrollEl = document.querySelector('.chat-messages');
    if (scrollEl) scrollEl.style.overflowAnchor = 'none';
    setSelectedTab(tab);
  };

  // Immediate scroll compensation before the browser paints the new tab content.
  useLayoutEffect(() => {
    const anchor = tabChangeAnchorRef.current;
    if (!anchor || !messageContainerRef.current) return;

    const scrollEl = document.querySelector('.chat-messages');
    if (!scrollEl) return;

    const newTop = messageContainerRef.current.getBoundingClientRect().top;
    const delta = newTop - anchor.containerTop;
    if (Math.abs(delta) > 0.5) {
      scrollEl.scrollTop += delta;
      // Update anchor so the ResizeObserver (below) doesn't double-correct.
      anchor.containerTop = messageContainerRef.current.getBoundingClientRect().top;
    }
  }, [selectedTab]);

  // ResizeObserver-based scroll stabilization.
  // A single tab switch can trigger MULTIPLE height changes:
  //   1. The initial content swap  (caught by useLayoutEffect above)
  //   2. Pagination state clearing / re-applying  (async useEffect)
  //   3. ServerSideDataTable hydrating its rows   (async setState)
  // The ResizeObserver fires on every height change and keeps the card
  // pinned to its original viewport position until the switch settles.
  // Cleanup is debounced: the anchor clears 150ms after the LAST resize,
  // so slow-loading content on any network is fully covered without a
  // fixed deadline that could expire too early.
  useEffect(() => {
    const el = messageContainerRef.current;
    const scrollEl = document.querySelector('.chat-messages');
    if (!el || !scrollEl) return;

    let settleTimer = null;

    const ro = new ResizeObserver(() => {
      const anchor = tabChangeAnchorRef.current;
      if (!anchor) return;

      const currentTop = el.getBoundingClientRect().top;
      const delta = currentTop - anchor.containerTop;
      if (Math.abs(delta) > 0.5) {
        scrollEl.scrollTop += delta;
        anchor.containerTop = el.getBoundingClientRect().top;
      }

      // Debounce: declare "settled" 150ms after the last resize event.
      // This adapts to how long content actually takes to load, rather
      // than an arbitrary fixed timeout.
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => {
        tabChangeAnchorRef.current = null;
        scrollEl.style.overflowAnchor = '';
      }, 150);
    });

    ro.observe(el);
    return () => {
      ro.disconnect();
      clearTimeout(settleTimer);
    };
  }, []);

  // Fallback: if no resize fires at all (new tab has the exact same
  // height as the old one), the ResizeObserver debounce never runs.
  // Restore native scroll anchoring after a short delay — intentionally
  // longer than the 150ms debounce so this only activates in the
  // no-resize edge case.
  useEffect(() => {
    if (!tabChangeAnchorRef.current) return;
    const id = setTimeout(() => {
      tabChangeAnchorRef.current = null;
      const scrollEl = document.querySelector('.chat-messages');
      if (scrollEl) scrollEl.style.overflowAnchor = '';
    }, 300);
    return () => clearTimeout(id);
  }, [selectedTab]);
  const [chartData, setChartData] = useState([]);
  const [feedback, setFeedback] = useState(initialFeedback);
  const [showModal, setShowModal] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [displayData, setDisplayData] = useState(() => {
    // For SQL and Insights datatypes, parse the message to get initial display data
    if (datatype === "sql" || datatype === "insights") {
      // Parse ALL data for client-side pagination (up to 30 rows from session API)
      return parseMessageToArray(message, null);
    }
    return Array.isArray(message) && message.length > 0 ? message.slice(0, 10) : [];
  });
  const [pageSize] = useState(10);
  const [paginationState, setPaginationState] = useState({ showPagination: false });
  const [activeSourceKey, setActiveSourceKey] = useState(null);
  const [docxPreview, setDocxPreview] = useState(null);
  const [highlightPreview, setHighlightPreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  // Which citation chunk is currently being opened. The ref guards against
  // synchronous double-clicks (state updates are async); the state drives the
  // pill's spinner/disabled UI.
  const [loadingChunkIdx, setLoadingChunkIdx] = useState(null);
  const loadingChunkRef = useRef(false);
  const isAuthorAdmin = authorId === 0;
  const isSQLDatatype = datatype === "sql";
  const isInsightsDatatype = datatype === "insights";

  // Infer answer_type from message data when not provided (e.g., loaded session history)
  const effectiveAnswerType = useMemo(() => {
    if (answer_type) return answer_type;
    // Only infer for completed bot messages (not during streaming)
    if (isStreaming || authorId !== 0) return null;
    // Has SQL queries or table data with source → sql
    if ((isSQLDatatype || isInsightsDatatype) && source) return 'sql';
    // Has curl commands → api
    if (curl_commands && curl_commands.length > 0) return 'api';
    // Has clarification options → clarification
    if (clarificationOptions && clarificationOptions.length > 0) return 'clarification';
    // Has RAG document references → rag
    if (reference?.chunks?.length > 0) return 'rag';
    // Has description/insights but no source → rag
    if (description && typeof description === 'string' && description.trim() !== '' && !source) return 'rag';
    // Insights datatype with no SQL source and no API commands → rag
    // (RAG fast mode may have empty description but still has insights datatype)
    if (isInsightsDatatype && !source && !(curl_commands && curl_commands.length > 0)) return 'rag';
    // Has SQL source or table data → sql
    if (isSQLDatatype || isInsightsDatatype) return 'sql';
    return null;
  }, [answer_type, isStreaming, authorId, isSQLDatatype, isInsightsDatatype, source, curl_commands, clarificationOptions, description, reference]);

  // Determine tab configuration from answer_type and analysis_mode
  const tabConfig = useMemo(() => {
    const hasSummary = analysis_mode === 'fast' && !!description?.trim();
    const hasInsights = !!description?.trim();
    return getTabConfig(effectiveAnswerType, analysis_mode, hasSummary, hasInsights);
  }, [effectiveAnswerType, analysis_mode, description]);
  const insightsTabLabel = analysis_mode === 'fast' ? 'Summary' : 'Insights';
  // Removed unused hasCurlCommands
  const serviceUrl = config.serviceUrl; // Use dynamic config
  const msgClass = "p-3";

  const [costData, setCostData] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState(null);
  const [showCostModal, setShowCostModal] = useState(false);

  const fetchTransactionCost = async () => {
    if (!id || costLoading || !config?.serviceUrl) return;
    setCostLoading(true);
    setCostError(null);
    setCostData(null);
    try {
      const serviceBase = config.serviceUrl.replace(/\/api\/v1\/?$/, '');
      const response = await axios.get(
        `${serviceBase}/v1/observability/transaction/${id}/cost`,
        { headers: { Authorization: `Bearer ${config.apiKey}` } }
      );
      const payload = response.data;
      if (payload?.code !== 0 || payload?.data == null) {
        setCostError(payload?.message || 'Transaction not found');
      } else {
        setCostData(payload.data);
      }
    } catch (error) {
      setCostError(error.response?.data?.message || error.message || 'Failed to fetch cost data');
    } finally {
      setCostLoading(false);
      setShowCostModal(true);
    }
  };
  
  // Infer total_count from displayData if totalRows is missing
  // This ensures consistent pagination behavior across all agents
  const effectiveTotalRows = useMemo(() => {
    if (totalRows != null) {
      return totalRows;
    }
    // If total_count is missing but we have displayData for SQL or Insights datatype,
    // infer it from displayData length (session API provides up to 30 rows)
    if ((isSQLDatatype || isInsightsDatatype) && displayData && displayData.length > 0) {
      return displayData.length;
    }
    return null;
  }, [totalRows, isSQLDatatype, isInsightsDatatype, displayData]);

  // For multi-table responses, parse each data source independently.
  // Entries with non-table content get empty arrays — MultiSourceTabs still renders
  // with the source keys so ServerSideDataTable can fetch via the pagination API.
  const perSourceData = useMemo(() => {
    if (!dataSourcesMap || typeof dataSourcesMap !== 'object') return null;
    const result = {};
    for (const [key, content] of Object.entries(dataSourcesMap)) {
      result[key] = parseMessageToArray(content, null) || [];
    }
    return Object.keys(result).length > 0 ? result : null;
  }, [dataSourcesMap]);

  // Build chunk-level citation map: chunk index → sequential citation number by first
  // appearance across message + description. Multiple chunks from the same document each
  // get their own number, so ##0$, ##2$, ##4$ from one PDF become [1], [2], [3].
  const { citationMap } = useMemo(() => {
    if (!reference?.chunks?.length) {
      return { citationMap: null, citedChunks: [] };
    }
    // Scan both message and description for ##N$ markers in order of first appearance
    const textsToScan = [message, description].filter(t => typeof t === 'string' && t);
    if (textsToScan.length === 0) {
      return { citationMap: null, citedChunks: [] };
    }
    const orderedIndices = [];
    const seen = new Set();
    const markerRe = /##(\d+)\$+/g;
    for (const text of textsToScan) {
      let m;
      markerRe.lastIndex = 0;
      while ((m = markerRe.exec(text)) !== null) {
        const idx = parseInt(m[1], 10);
        if (!seen.has(idx) && idx < reference.chunks.length) {
          seen.add(idx);
          orderedIndices.push(idx);
        }
      }
    }
    // map[chunkIdx] = 1-based citation number by order of first appearance
    const map = {};
    orderedIndices.forEach((chunkIdx, i) => { map[chunkIdx] = i + 1; });
    // ordered list of cited chunk objects for source pills
    const cited = orderedIndices.map(idx => ({ ...reference.chunks[idx], _chunkIdx: idx }));
    return { citationMap: map, citedChunks: cited };
  }, [message, description, reference]);

  // Replace ##N$$ citation markers with inline clickable [i](#cite-idx) links.
  // Using #cite-idx instead of cite:idx to guarantee it passes SAFE_HREF_RE and ReactMarkdown
  // parses it unambiguously as an inline link (cite: is non-standard and may be normalised away).
  const replaceCitationMarkers = useCallback((text) => {
    if (!citationMap || !text || typeof text !== 'string') return text;
    return text.replace(/ *(##(\d+)\$+) */g, (match, _full, numStr) => {
      const idx = parseInt(numStr, 10);
      const pad = match.startsWith(' ') ? ' ' : '';
      return citationMap[idx] !== undefined ? `${pad}[${citationMap[idx]}](#cite-${idx})` : pad;
    });
  }, [citationMap]);

  const replaceHTMLCitationMarkers = useCallback((html) => {
    if (!citationMap || !html || typeof html !== 'string') return html;
    const bg = isDarkMode ? 'rgba(14,165,233,0.2)' : '#e0f2fe';
    const color = isDarkMode ? '#7dd3fc' : '#0369a1';
    return html.replace(/([ ]*)##(\d+)\$+([ ]*)(?=[^$]|$)/g, (_match, spaceBefore, numStr, spaceAfter) => {
      const idx = parseInt(numStr, 10);
      if (citationMap[idx] === undefined) return spaceBefore;
      const style = `display:inline-flex;align-items:center;justify-content:center;vertical-align:super;font-size:10px;font-weight:600;line-height:1;border-radius:9999px;width:16px;height:16px;min-width:16px;margin:0 2px;background-color:${bg};color:${color};cursor:pointer;text-decoration:none;`;
      return spaceBefore + `<a href="#cite-${idx}" style="${style}" title="View source">${citationMap[idx]}</a>` + spaceAfter;
    });
  }, [citationMap, isDarkMode]);

  const processedMessage = useMemo(() => replaceCitationMarkers(message), [message, replaceCitationMarkers]);
  const processedDescription = useMemo(() => {
    if (!description || typeof description !== 'string') return description;
    if (description.trimStart().startsWith('<')) return replaceHTMLCitationMarkers(description);
    return replaceCitationMarkers(description);
  }, [description, replaceCitationMarkers, replaceHTMLCitationMarkers]);

  const handleSourceClick = useCallback(async (chunkIdx) => {
    // Ignore clicks while another citation is already opening so repeated
    // clicks don't fire duplicate signed-URL requests / open stacked modals.
    if (loadingChunkRef.current) return;
    const chunk = reference?.chunks?.[chunkIdx];
    if (!chunk) return;
    const docId = chunk.document_id || chunk.doc_id;
    if (!docId) return;
    loadingChunkRef.current = true;
    setLoadingChunkIdx(chunkIdx);
    try {
    let baseUrl = '';
    try { baseUrl = new URL(config.serviceUrl).origin; } catch { /* keep empty */ }

    // `#page=N` jumps PDF viewers to the cited page. When the cited chunk
    // itself has no positions (content-free nav/heading chunk), fall back to
    // the first page from any sibling chunk of the same document.
    let pageNum = chunk.positions?.[0]?.[0];
    if (!pageNum) {
      const sibling = reference?.chunks?.find(
        (c) => (c?.document_id || c?.doc_id) === docId && c?.positions?.[0]?.[0]
      );
      pageNum = sibling?.positions?.[0]?.[0];
    }
    const fragment = pageNum ? `#page=${pageNum}` : '';

    const VIDEO_EXTS = ['mp4', 'webm', 'ogg', 'ogv', 'm4v', 'mov'];
    const firstPos = chunk.positions?.find((p) => Array.isArray(p) && p.length >= 5 && p[0]);
    const hasBbox = firstPos && (firstPos[1] || firstPos[2] || firstPos[3] || firstPos[4]);

    const authHeader = config.apiKey ? `Bearer ${config.apiKey}` : '';

    // Resolve a direct-fetch URL via the signed-URL endpoint so the file is
    // loaded from the user's own storage (S3 / GCS / MinIO) instead of being
    // streamed through Discover. Falls back to the proxy URL for providers
    // that don't expose signed URLs (Google Drive, GCS user-OAuth).
    const proxyUrl = `${baseUrl}/v1/document/get/${docId}`;
    let resolved = { url: proxyUrl, source: 'proxy', provider: 'unknown' };
    try {
      const res = await fetch(`${baseUrl}/v1/document/signed_url/${docId}`, {
        credentials: 'include',
        headers: authHeader ? { Authorization: authHeader } : undefined,
      });
      if (res.ok) {
        const payload = await res.json();
        // /signed_url returns { code, data: { url, source, provider, expires_in } }
        const data = payload?.data || payload;
        if (data?.url) resolved = data;
      }
    } catch {
      // Network/JSON error — fall back to proxy URL.
    }

    // Knowledge-graph chunks (entities/relationships) carry a real
    // `document_id` but an empty `document_name`, so we can't tell the file
    // type up front. Recover both the display name and the extension from the
    // resolved URL's path — the signed/proxy URL ends in the real filename.
    let docName = chunk.document_name || chunk.docnm_kwd || '';
    let ext = docName.split('.').pop()?.toLowerCase() || '';
    if (!docName || !ext || ext === docName.toLowerCase()) {
      try {
        const path = new URL(resolved.url, baseUrl || window.location.origin).pathname;
        const base = decodeURIComponent(path.split('/').pop() || '');
        const urlExt = base.includes('.') ? base.split('.').pop().toLowerCase() : '';
        if (!docName && base) docName = base;
        if (urlExt && urlExt.length <= 5) ext = urlExt;
      } catch { /* keep what we have */ }
    }
    if (!docName) docName = 'Document';
    const isVideo = VIDEO_EXTS.includes(ext);

    // The "Open full ..." CTA navigates a new tab straight to this URL, which
    // can't carry a bearer header. So it only works for real cloud/signed URLs;
    // when the signed-URL lookup fell back to the auth proxy there is no
    // externally-openable URL and the CTA must be hidden (otherwise the new tab
    // shows the backend's "Authentication required" error).
    const externalUrl = resolved.source === 'proxy' ? null : resolved.url;

    // Highlight-preview modal: only meaningful for PDFs with real bbox
    // positions. PNG is rendered server-side from the cited page; the full
    // doc opens via the resolved (cloud) URL when the user clicks the CTA.
    // PDF modal: render the whole document inline via the backend proxy and
    // highlight the cited chunk — using its real bbox coordinates when present,
    // otherwise aligning the chunk text against the rendered text layer. Same
    // privacy model as the DOCX proxy (bytes streamed in-memory). The "Open
    // full document" link still points at the cloud URL via the user's session.
    if (ext === 'pdf') {
      setPdfPreview({
        docName,
        fetchUrl: `${proxyUrl}?stream=1`,
        downloadUrl: `${proxyUrl}?stream=1`,
        fullDocUrl: externalUrl ? externalUrl + fragment : null,
        source: resolved.source,
        provider: resolved.provider,
        chunkContent: chunk.content || '',
        chunkPositions: chunk.positions || [],
        hasBbox: Boolean(hasBbox),
        authHeader,
      });
      return;
    }

    // DOCX modal: always rendered via the backend proxy (uses stored connector
    // token, in-memory only — same privacy model as our Drive/GCS PDF
    // proxy). The "Open full document" CTA still points at the cloud URL so
    // the user can hit storage.cloud.google.com / drive.google.com / S3
    // signed URL via their own browser session.
    if (ext === 'docx') {
      // ?stream=1 forces the backend to proxy the bytes instead of 302-ing to
      // the cloud URL (which docx-preview can't fetch because of CORS on
      // storage.cloud.google.com / drive.google.com).
      setDocxPreview({
        docName,
        fetchUrl: `${proxyUrl}?stream=1`,
        downloadUrl: `${proxyUrl}?stream=1`,
        fullDocUrl: externalUrl ? externalUrl + fragment : null,
        source: resolved.source,
        provider: resolved.provider,
        chunkContent: chunk.content || '',
        authHeader,
      });
      return;
    }

    // Video modal: play the resolved URL inline with a <video> element. Signed
    // cloud URLs stream cross-origin without CORS; the same-origin proxy URL
    // sends cookies automatically. Download/Open CTAs cover the bearer-auth
    // case the media element can't handle.
    if (isVideo) {
      setVideoPreview({
        docName,
        videoUrl: resolved.url,
        downloadUrl: `${proxyUrl}?stream=1`,
        fullDocUrl: externalUrl,
        source: resolved.source,
        provider: resolved.provider,
        authHeader,
      });
      return;
    }

    // Generic fallback: no purpose-built preview applies (PDF without bbox,
    // unknown/extension-less type, etc.). Still open a modal "no matter what"
    // by framing the resolved document URL — browsers render PDFs and images
    // inline; framing-blocked providers fall back to the modal's "Open in new
    // tab" CTA. `#page=N` is honoured by the embedded PDF viewer.
    setFilePreview({
      docName,
      previewUrl: resolved.url + fragment,
      downloadUrl: `${proxyUrl}?stream=1`,
      fullDocUrl: externalUrl ? externalUrl + fragment : null,
      source: resolved.source,
      provider: resolved.provider,
      authHeader,
      ext,
    });
    } finally {
      // Cleared once the preview state is set (modals open synchronously after
      // this); re-enables the pills for the next citation.
      loadingChunkRef.current = false;
      setLoadingChunkIdx(null);
    }
  }, [reference, config.serviceUrl, config.apiKey]);

  const token = localStorage.getItem("token");

  // Initialize displayData when message changes
  // For SQL and Insights datatypes, parse ALL data for client-side pagination (session API provides up to 30 rows)
  useEffect(() => {
    if (isSQLDatatype || isInsightsDatatype) {
      const parsed = parseMessageToArray(message, null) || [];
      if (parsed.length > 0) {
        // Always update when there is real data
        setDisplayData(parsed);
      } else if (!isStreaming) {
        // Only clear to empty after streaming completes — avoids flicker when
        // an intermediate non-table chunk temporarily sets message to ""
        setDisplayData(parsed);
      }
      // If streaming and parsed is empty, keep the existing displayData so the
      // Data tab continues showing the last known records during the transition.
    }
  }, [message, isSQLDatatype, isInsightsDatatype, isStreaming]);

  // For non-SQL/non-Insights datatypes, limit displayed data based on pageSize
  useEffect(() => {
    if (!isSQLDatatype && !isInsightsDatatype && Array.isArray(message) && message.length > 0) {
      setDisplayData(message.slice(0, pageSize));
    }
  }, [message, pageSize, isSQLDatatype, isInsightsDatatype]);

  // Update feedback state when initialFeedback prop changes (e.g., when loading a session)
  useEffect(() => {
    setFeedback(initialFeedback);
  }, [initialFeedback, id]);

  // Clear pagination state when switching away from data tab
  useEffect(() => {
    if (selectedTab !== 'data') {
      setPaginationState({ showPagination: false });
    }
  }, [selectedTab]);

  // If currently selected tab is not in the visible tabs list, reset to default
  useEffect(() => {
    if (tabConfig.showTabs && tabConfig.visibleTabs.length > 0 && !tabConfig.visibleTabs.includes(selectedTab)) {
      setSelectedTab(tabConfig.defaultTab || tabConfig.visibleTabs[0]);
    }
  }, [tabConfig.showTabs, tabConfig.visibleTabs, tabConfig.defaultTab, selectedTab]);

  // Auto-switch to insights tab when content first arrives, unless user already picked a tab
  useEffect(() => {
    if (!hasUserSelectedTab.current && tabConfig.defaultTab === 'description' && tabConfig.visibleTabs.includes('description')) {
      setSelectedTab('description');
    }
  }, [tabConfig.defaultTab, tabConfig.visibleTabs]);

  const handleClarificationOptionClick = (option) => {
    if (onClarificationOptionClick) {
      onClarificationOptionClick(option, id);
    }
  };

  // Utility functions
  const isTable = () => {
    try {
      if (typeof message === "string" && message.trim().startsWith("|")) {
        const firstLine = message.trim().split("\n")[0];
        return firstLine.split("|").length > 2;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  const containsMarkdown = () => {
    if (typeof message !== "string") return false;
    // Citation markers (##N$$) will be converted to markdown links in processedMessage
    if (/##\d+\$+/.test(message)) return true;
    const markdownPatterns = [
      /^#+\s/,
      /\*\*.+\*\*/,
      /_.+_/,
      /`[^`]+`/,
      /```[\s\S]+```/,
      /^\s*[-*+]\s/,
      /^\s*\d+\.\s/,
      /\[.+\]\(.+\)/,
      /!\[.+\]\(.+\)/,
      /\|.*\|.*\|/,
      /^>\s/,
    ];
    return markdownPatterns.some((pattern) => pattern.test(message));
  };

  const sendMessageFeedback = async (sessionId, messageId, feedback, feedbackMessage = "") => {
    try {
      const response = await axios.put(
        `${config.serviceUrl}/agents/${config.agentId}/messages/feedback`,
        { session_id: sessionId, message_id: messageId, feedback: feedback, feedback_message: feedbackMessage },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${config.apiKey}` } }
      );
      return response.data;
    } catch (error) {
      console.error("Error sending feedback:", error);
      console.error("Error details:", error.response?.data);
      return null;
    }
  };

  const handleFeedback = async (feedbackValue, comment = "") => {
    try {
      const newFeedback = feedback === feedbackValue ? 0 : feedbackValue;
      
      // Send the NEW feedback value to the API (not the clicked value)
      const response = await sendMessageFeedback(chatSessionId, id, newFeedback, comment || "");
      if (response) {
        setFeedback(newFeedback);
        
        // Save to localStorage as backup (until backend returns feedback in API)
        try {
          const FEEDBACK_STORAGE_KEY = 'message_feedback_cache';
          const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
          const feedbackCache = stored ? JSON.parse(stored) : {};
          if (newFeedback === 0) {
            delete feedbackCache[id];
          } else {
            feedbackCache[id] = newFeedback;
          }
          localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(feedbackCache));
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
        }
        
        // Update the liveChat state to persist feedback in current session
        setLiveChat((prevMessages) => 
          prevMessages.map((msg) => 
            msg.id === id ? { ...msg, feedback: newFeedback } : msg
          )
        );
      } else {
        console.error(`Failed to save feedback for message ${id}`);
      }
    } catch (error) {
      console.error("Error handling feedback:", error);
    }
  };

  const onReply = async () => {
    // Convert source or message to a displayable string
    let quotedContent = isSQLDatatype ? source : message;
    
    // Handle arrays of objects (like SQL queries with component_id and query)
    if (Array.isArray(quotedContent)) {
      quotedContent = quotedContent.map((item) => {
        if (typeof item === "object" && item !== null) {
          return item.query || JSON.stringify(item);
        }
        return String(item);
      }).join("\n");
    } else if (typeof quotedContent === "object" && quotedContent !== null) {
      quotedContent = quotedContent.query || JSON.stringify(quotedContent);
    }
    
    setQuotedMessage(quotedContent);
    setCurrentParentId(id);
    await resizeInput();
  };

  const handleSend = async (response) => {
    const tempChatId = id + Date.now().toString();
    const requestBody = {
      id: tempChatId,
      message: response,
      chat_session_id: chatSessionId,
      parent_message_id: setCurrentParentId,
      author_id: authorId,
    };
    setLiveChat((prevMessage) => [...prevMessage, requestBody]);
    setQuotedMessage(null);
    setCurrentParentId(null);
    resizeInput();

    const config_home = {
      headers: { "Content-Type": "application/json", Authorization: token },
    };

    try {
      const response = await axios.post(serviceUrl, requestBody, config_home);
      setLiveChat((prevMessages) => {
        const updatedMessages = [...prevMessages];
        const lastMessageIndex = updatedMessages.length - 1;
        updatedMessages[lastMessageIndex].id = response.data.user_message_id;
        return updatedMessages;
      });
      chatSessionId = response.data.chat_session_id;
      setLiveChat((prevMessage) => [...prevMessage, response.data.bot_message]);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleDislikeClick = () => {
    // If already disliked, toggle back to neutral
    if (feedback === -1) {
      handleFeedback(-1, ""); // This will toggle to 0 (neutral)
    } else {
      // Not disliked yet, show modal for comment
      setShowModal(true);
    }
  };

  const handleModalSubmit = async () => {
    if (feedbackComment.trim().length < 1 || isSubmittingFeedback) return;
    
    setIsSubmittingFeedback(true);
    try {
      await handleFeedback(-1, feedbackComment);
      setFeedbackComment("");
      setShowModal(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleModalClose = () => {
    // Close without saving feedback
    setFeedbackComment("");
    setShowModal(false);
    setIsSubmittingFeedback(false);
  };

  // Helper function to safely render parent message text
  const getParentMessageDisplay = () => {
    if (!parentMessage) return null;
    
    const text = parentMessage.type === "sql" 
      ? parentMessage.source 
      : parentMessage.message;
    
    // Handle different types of content
    if (typeof text === "string") {
      return text;
    } else if (Array.isArray(text)) {
      return text.map((item) => {
        // If item is an object with a query property, extract it
        if (typeof item === "object" && item !== null) {
          if (item.query) {
            return item.query;
          }
          // Otherwise stringify the object
          return JSON.stringify(item);
        }
        return String(item);
      }).join("\n---\n");
    } else if (typeof text === "object" && text !== null) {
      // If it's a single object with query property
      if (text.query) {
        return text.query;
      }
      return JSON.stringify(text, null, 2);
    }
    
    return String(text || "");
  };

  const parentMessageText = getParentMessageDisplay();

  return (
    <CitationLoadingContext.Provider value={loadingChunkIdx}>
    <WebSourcesContext.Provider value={reference?.web_sources ?? null}>
    <div className={`${msgClass} w-full flex ${isAuthorAdmin ? 'justify-start' : 'justify-end'}`}>
      <div className={`flex items-start gap-3 ${isAuthorAdmin ? 'max-w-4xl' : 'flex-col items-end'}`}>
        {!isAuthorAdmin && (
          <div className="flex items-start gap-3">
            <div>
              {parentMessageText && (
                <div className={`mb-3 p-3 rounded-lg border-l-4 ${
                  isDarkMode ? 'bg-slate-800/50 border-blue-500' : 'bg-blue-50 border-blue-400'
                }`}>
                  <p className={`text-xs font-semibold mb-1 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`}>
                    Replying To:
                  </p>
                  <p className={`text-sm line-clamp-2 whitespace-pre-wrap ${
                    isDarkMode ? 'text-slate-300' : 'text-gray-700'
                  }`}>
                    {parentMessageText}
                  </p>
                </div>
              )}
              <div 
                className="rounded-2xl px-4 py-3 max-w-4xl text-white shadow-sm"
                style={{
                  background: isDarkMode 
                    ? '#334155' 
                    : 'linear-gradient(135deg, rgb(74, 111, 165) 0%, rgb(53, 122, 143) 100%)'
                }}
              >
                <div className="text-sm leading-relaxed break-words whitespace-pre-wrap">
                  {isJSONResponse(message) ? (
                    <div className="markdown-content break-words">
                      <MarkdownRenderer content={parseJSONResponse(message)} />
                    </div>
                  ) : typeof message === "object" ? (
                    JSON.stringify(message)
                  ) : (
                    message
                  )}
                </div>
              </div>
              {/* Timestamp for user messages */}
              <DynamicTimestamp 
                timestamp={timestamp} 
                isDarkMode={isDarkMode} 
                isUserMessage={true}
              />
            </div>
            <div className="flex-shrink-0">
              <UserAvatar 
                photoURL={avatar} 
                userName={author} 
                isDarkMode={isDarkMode} 
              />
            </div>
          </div>
        )}
        {isAuthorAdmin && (
          <div className="flex-shrink-0">
            {/* Display icon for bot's avatar - shows "D" for Discover */}
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-base ${
              isDarkMode 
                ? 'bg-gradient-to-br from-cyan-500 to-blue-600 border-2 border-slate-700 shadow-lg' 
                : 'bg-gradient-to-br from-cyan-400 to-sky-500 border-2 border-gray-200 shadow-md'
            }`}>
              D
            </div>
          </div>
        )}
        
        {isAuthorAdmin && (
          <div className={'flex-1 min-w-0'}>
          {parentMessageText && (
            <div className={`mb-3 p-3 rounded-lg border-l-4 ${
              isDarkMode ? 'bg-slate-800/50 border-cyan-500' : 'bg-cyan-50 border-cyan-400'
            }`}>
              <p className={`text-xs font-semibold mb-1 ${
                isDarkMode ? 'text-cyan-400' : 'text-cyan-600'
              }`}>
                Replying To:
              </p>
              <p className={`text-sm line-clamp-2 whitespace-pre-wrap ${
                isDarkMode ? 'text-slate-300' : 'text-gray-700'
              }`}>
                {parentMessageText}
              </p>
            </div>
          )}
          
          <ReasoningSteps steps={reasoning_steps} isStreaming={isStreaming} isDarkMode={isDarkMode} />

          <div ref={messageContainerRef} className={`rounded-xl overflow-hidden transition-colors transition-shadow duration-300 ${
            isDarkMode
              ? 'bg-slate-800/80 shadow-lg shadow-black/30'
              : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            {tabConfig.showTabs && (
              <ChatMessageTabs
                selectedTab={selectedTab}
                setSelectedTab={handleTabChange}
                visibleTabs={tabConfig.visibleTabs}
                insightsLabel={insightsTabLabel}
              />
            )}
            <div className={`p-3 overflow-x-auto ${
              isDarkMode ? 'text-slate-200' : 'text-gray-800'
            }`}>
              <div className="max-h-[70vh] overflow-y-auto">
                {tabConfig.showTabs ? (
                  <TabContent
                    selectedTab={selectedTab}
                    id={id}
                    isSQLDatatype={isSQLDatatype}
                    isInsightsDatatype={isInsightsDatatype}
                    totalRows={effectiveTotalRows}
                    displayData={displayData}
                    message={processedMessage}
                    isTable={isTable}
                    containsMarkdown={containsMarkdown}
                    handleSend={handleSend}
                    isAuthorAdmin={isAuthorAdmin}
                    clarificationOptions={clarificationOptions}
                    handleClarificationOptionClick={handleClarificationOptionClick}
                    clarificationOptionsDisabled={clarificationOptionsDisabled}
                    selectedClarificationOption={selectedClarificationOption}
                    chartData={chartData}
                    setChartData={setChartData}
                    source={source}
                    datatype={datatype}
                    description={processedDescription}
                    curl_commands={curl_commands}
                    setPaginationState={setPaginationState}
                    sessionId={chatSessionId}
                    isStreaming={isStreaming}
                    perSourceData={perSourceData}
                    effectiveTotalRows={effectiveTotalRows}
                    onChunkClick={handleSourceClick}
                    onActiveSourceChange={setActiveSourceKey}
                  />
                ) : (
                  <DirectContent
                    answer_type={effectiveAnswerType}
                    analysisMode={analysis_mode}
                    message={processedMessage}
                    description={processedDescription}
                    dataSourcesMap={dataSourcesMap}
                    replaceCitationMarkers={replaceCitationMarkers}
                    isTable={isTable}
                    containsMarkdown={containsMarkdown}
                    id={id}
                    handleSend={handleSend}
                    isAuthorAdmin={isAuthorAdmin}
                    clarificationOptions={clarificationOptions}
                    handleClarificationOptionClick={handleClarificationOptionClick}
                    clarificationOptionsDisabled={clarificationOptionsDisabled}
                    selectedClarificationOption={selectedClarificationOption}
                    setPaginationState={setPaginationState}
                    isStreaming={isStreaming}
                    onChunkClick={handleSourceClick}
                  />
                )}
              </div>
              <WebGroundingPanel
                searchEntryPointHtml={reference?.search_entry_point?.rendered_content}
                isDarkMode={isDarkMode}
              />
            </div>
            <ActionButtons
              selectedTab={tabConfig.showTabs ? selectedTab : (effectiveAnswerType === 'rag' ? 'description' : 'data')}
              isSQLDatatype={isSQLDatatype}
              source={source}
              message={message}
              description={description}
              dataSourcesMap={dataSourcesMap}
              curl_commands={curl_commands}
              onReply={onReply}
              isAuthorAdmin={isAuthorAdmin}
              feedback={feedback}
              handleFeedback={handleFeedback}
              handleDislikeClick={handleDislikeClick}
              id={id}
              chatSessionId={chatSessionId}
              paginationState={paginationState}
              isStreaming={isStreaming}
              activeSourceKey={activeSourceKey}
              isMultiSource={!!(perSourceData && Object.keys(perSourceData).length > 1)}
              onShowCost={fetchTransactionCost}
              costLoading={costLoading}
            />
          </div>
          {/* Timestamp for bot messages */}
          <DynamicTimestamp 
            timestamp={timestamp} 
            isDarkMode={isDarkMode} 
            isUserMessage={false}
          />
        </div>
        )}
      </div>

      <FeedbackModal
        showModal={showModal}
        feedbackComment={feedbackComment}
        setFeedbackComment={setFeedbackComment}
        handleModalSubmit={handleModalSubmit}
        handleModalClose={handleModalClose}
        isSubmitting={isSubmittingFeedback}
      />
      {showCostModal && (
        <CostModal
          costData={costData}
          costError={costError}
          isDarkMode={isDarkMode}
          onClose={() => setShowCostModal(false)}
        />
      )}
      {docxPreview && (
        <DocxPreviewModal
          docName={docxPreview.docName}
          fetchUrl={docxPreview.fetchUrl}
          downloadUrl={docxPreview.downloadUrl}
          fullDocUrl={docxPreview.fullDocUrl}
          source={docxPreview.source}
          provider={docxPreview.provider}
          chunkContent={docxPreview.chunkContent}
          authHeader={docxPreview.authHeader}
          onClose={() => setDocxPreview(null)}
        />
      )}
      {pdfPreview && (
        <PdfPreviewModal
          docName={pdfPreview.docName}
          fetchUrl={pdfPreview.fetchUrl}
          downloadUrl={pdfPreview.downloadUrl}
          fullDocUrl={pdfPreview.fullDocUrl}
          source={pdfPreview.source}
          provider={pdfPreview.provider}
          chunkContent={pdfPreview.chunkContent}
          chunkPositions={pdfPreview.chunkPositions}
          hasBbox={pdfPreview.hasBbox}
          authHeader={pdfPreview.authHeader}
          onClose={() => setPdfPreview(null)}
        />
      )}
      {highlightPreview && (
        <HighlightPreviewModal
          docName={highlightPreview.docName}
          highlightUrl={highlightPreview.highlightUrl}
          fullDocUrl={highlightPreview.fullDocUrl}
          proxyUrl={highlightPreview.proxyUrl}
          source={highlightPreview.source}
          provider={highlightPreview.provider}
          authHeader={highlightPreview.authHeader}
          onClose={() => setHighlightPreview(null)}
        />
      )}
      {videoPreview && (
        <VideoPreviewModal
          docName={videoPreview.docName}
          videoUrl={videoPreview.videoUrl}
          downloadUrl={videoPreview.downloadUrl}
          fullDocUrl={videoPreview.fullDocUrl}
          source={videoPreview.source}
          provider={videoPreview.provider}
          startTime={videoPreview.startTime}
          authHeader={videoPreview.authHeader}
          onClose={() => setVideoPreview(null)}
        />
      )}
      {filePreview && (
        <FilePreviewModal
          docName={filePreview.docName}
          previewUrl={filePreview.previewUrl}
          downloadUrl={filePreview.downloadUrl}
          fullDocUrl={filePreview.fullDocUrl}
          source={filePreview.source}
          provider={filePreview.provider}
          authHeader={filePreview.authHeader}
          ext={filePreview.ext}
          onClose={() => setFilePreview(null)}
        />
      )}
    </div>
    </WebSourcesContext.Provider>
    </CitationLoadingContext.Provider>
  );
};

ChatMessage.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  authorId: PropTypes.number.isRequired,
  author: PropTypes.string.isRequired,
  avatar: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
  source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  description: PropTypes.string,
  totalRows: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  dataSourcesMap: PropTypes.object,
  datatype: PropTypes.string,
  parentMessage: PropTypes.shape({
    type: PropTypes.string,
    source: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  }),
  resizeInput: PropTypes.func.isRequired,
  setQuotedMessage: PropTypes.func.isRequired,
  setCurrentParentId: PropTypes.func.isRequired,
  chatSessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setLiveChat: PropTypes.func.isRequired,
  clarificationOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  onClarificationOptionClick: PropTypes.func,
  clarificationOptionsDisabled: PropTypes.bool,
  selectedClarificationOption: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.number]),
  curl_commands: PropTypes.arrayOf(
    PropTypes.shape({
      component_id: PropTypes.string,
      curl: PropTypes.string,
      url: PropTypes.string
    })
  ),
  initialFeedback: PropTypes.number,
  timestamp: PropTypes.number,
  isStreaming: PropTypes.bool,
  analysis_mode: PropTypes.oneOf(["deep", "fast"]),
  reference: PropTypes.shape({
    chunks: PropTypes.arrayOf(PropTypes.object),
    doc_aggs: PropTypes.arrayOf(PropTypes.object),
    // Populated when Generate had google_search grounding enabled.
    // See https://ai.google.dev/gemini-api/docs/google-search
    web_sources: PropTypes.arrayOf(PropTypes.shape({
      index: PropTypes.number,
      uri: PropTypes.string,
      title: PropTypes.string,
    })),
    web_search_queries: PropTypes.arrayOf(PropTypes.string),
    search_entry_point: PropTypes.shape({
      rendered_content: PropTypes.string,
    }),
  }),
  answer_type: PropTypes.string,
  reasoning_steps: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.oneOf(['thought', 'tool', 'status']),
    content: PropTypes.string,
    tool: PropTypes.string,
    status: PropTypes.string,
    ts: PropTypes.number,
  })),
};

ChatMessage.defaultProps = {
  source: null,
  description: null,
  totalRows: null,
  datatype: null,
  parentMessage: null,
  clarificationOptions: [],
  onClarificationOptionClick: null,
  clarificationOptionsDisabled: false,
  selectedClarificationOption: null,
  curl_commands: [],
  initialFeedback: 0, // Add initialFeedback default prop
};

export default ChatMessage;